import { useState, useEffect, useRef } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useUnsavedChanges } from "hooks/useUnsavedChanges";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Collapse from "@mui/material/Collapse";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import InboxIcon from "@mui/icons-material/MoveToInbox";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import AddIcon from "@mui/icons-material/Add";
import Fab from "@mui/material/Fab";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiBadge from "components/VuiBadge";
import VuiButton from "components/VuiButton";
import LoadingSpinner from "components/LoadingSpinner";
import RelatedPartiesTab from "components/RelatedPartiesTab";
import ConfirmationStatementTab from "components/ConfirmationStatementTab";
import ChargesTab from "components/ChargesTab";
import SecretsTab from "components/SecretsTab";
import DocumentsTab from "components/DocumentsTab";
import Ad01FilingWizard from "components/Ad01FilingWizard";
import { useAuthz } from "context/AuthzContext";
import { useVisionUIController } from "context";
import { PERMISSIONS } from "auth/permissions";
import {
  getClientProfile,
  apiRequest,
  listClientProfileConsents,
  listDirectors,
  resyncClientFromCH,
  listTenantUsers,
  updateClientProfile,
  previewRecordRequest,
  sendRecordRequest,
  listRecordRequests,
  listClientInbox,
  deleteClientInboxEmail,
  hardDeleteClientInboxEmail,
  sendClientEmail,
  triggerEmailSync,
  getAttachmentUrl,
  listMasterServices,
  listClientServices,
  assignClientService,
  removeClientService,
  listClientJobs,
  createClientJob,
  listJobTasks,
  updateJobTask,
  updateClientJob,
  getFilingDocumentUrl,
  getEmailTemplates,
  deleteClientProfilePermanent,
} from "services/tenantUserAdminService";
import { TC, FS, FW } from "../tokens/text";
import HMRCOAuthConnect from "../components/HMRCOAuthConnect";
import AssistantPane from "components/AssistantPane";

// ─── Constants ────────────────────────────────────────────────────────────────

const BUSINESS_TYPES = [
  { value: "ltd",         label: "Limited Company (Ltd)" },
  { value: "llp",         label: "LLP" },
  { value: "sole_trader", label: "Sole Trader" },
  { value: "partnership", label: "Partnership" },
  { value: "other",       label: "Other" },
];
const JURISDICTIONS = [
  { value: "UK", label: "United Kingdom" }, { value: "IE", label: "Ireland" },
  { value: "DE", label: "Germany" },        { value: "FR", label: "France" },
  { value: "NL", label: "Netherlands" },    { value: "other", label: "Other" },
];
const CURRENCIES      = [{ value: "GBP", label: "GBP £" }, { value: "EUR", label: "EUR €" }, { value: "USD", label: "USD $" }];
const VAT_SCHEMES     = [{ value: "none", label: "No VAT" }, { value: "cash", label: "Cash Accounting" }, { value: "standard", label: "Accruals" }, { value: "flat_rate", label: "Flat Rate" }];
const ACCOUNTING_BASIS = [{ value: "accrual", label: "Accrual Basis" }, { value: "cash", label: "Cash Basis" }];
const ACCESS_LEVELS   = [{ value: "partner", label: "Partner" }, { value: "senior", label: "Senior Accountant" }, { value: "reviewer", label: "Reviewer" }, { value: "read_only", label: "Read Only" }];
const ONBOARDING_STATUS = [{ value: "pending", label: "Pending" }, { value: "partial", label: "Partial" }, { value: "complete", label: "Complete" }];

const labelFor = (list, val) => list.find((i) => i.value === val)?.label || val || "—";

function getProfileVatValue(profile, flatKey, nestedKey, fallback = "") {
  const flatValue = profile?.[flatKey];
  if (flatValue !== undefined && flatValue !== null && flatValue !== "") return flatValue;

  const nestedValue = profile?.tax_identity?.[nestedKey];
  if (nestedValue !== undefined && nestedValue !== null && nestedValue !== "") return nestedValue;

  return fallback;
}

const VAT_STAGE_LABELS = {
  AwaitingObligation: "Awaiting HMRC obligation",
  RequestSent: "Documents requested",
  ReminderSent: "Reminder sent",
  DocumentsReceived: "Documents received",
  PreparingVat: "Preparing VAT",
  ReadyToSubmit: "Ready to submit",
  Submitted: "Submitted",
  Completed: "Completed",
};

function formatVatStage(stage) {
  return VAT_STAGE_LABELS[stage] || stage || "Awaiting HMRC obligation";
}

// Some senders' HTML-to-text conversion (e.g. table-based layouts used purely
// for spacing) leaves behind lines made up of only "|" characters. Strip those
// spacer artifacts and collapse the resulting blank-line runs.
function cleanEmailBodyText(text) {
  if (!text) return text;
  const lines = text.split("\n").map((line) => (/^[\s|]*$/.test(line) ? "" : line));
  const cleaned = [];
  let blankRun = 0;
  for (const line of lines) {
    if (line.trim() === "") {
      blankRun += 1;
      if (blankRun <= 2) cleaned.push("");
    } else {
      blankRun = 0;
      cleaned.push(line);
    }
  }
  return cleaned.join("\n").trim();
}

// Attachment preview classification. Some senders' mail clients attach files
// with a generic/incorrect Content-Type (e.g. "application/octet-stream"), so
// fall back to the filename extension when the declared type is unrecognized.
function classifyAttachmentPreview(contentType, name) {
  const ct = (contentType || "").toLowerCase();
  const ext = (name || "").toLowerCase().match(/\.[a-z0-9]+$/)?.[0] || "";

  const isImage = ct.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(ext);
  if (isImage) return "image";

  const isPdf = ct === "application/pdf" || ext === ".pdf";
  if (isPdf) return "pdf";

  const isText = ct.startsWith("text/") || [".txt", ".csv", ".log"].includes(ext);
  if (isText) return "text";

  const isOffice = /msword|wordprocessingml|ms-excel|spreadsheetml|ms-powerpoint|presentationml/.test(ct)
    || [".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx"].includes(ext);
  if (isOffice) return "office";

  return "unknown";
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <VuiTypography
      variant="caption" fontWeight="bold"
      sx={{ color: TC.primary, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: FS.sm, userSelect: "none" }}
    >
      {children}
    </VuiTypography>
  );
}

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <Grid item xs={12} sm={6} md={4}>
      <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm, display: "block", mb: 0.2, userSelect: "none" }}>{label}</VuiTypography>
      <VuiTypography variant="button" fontWeight="medium" sx={{ color: TC.primary }}>{value}</VuiTypography>
    </Grid>
  );
}

function SectionCard({ children, sx = {} }) {
  return (
    <VuiBox
      p={2.5}
      sx={{
        background: "#ffffff",
        border: "1px solid #d8e6f7",
        borderRadius: "12px",
        ...sx,
      }}
    >
      {children}
    </VuiBox>
  );
}

function CollapsibleSection({ title, titleChip, headerAction, defaultOpen = false, id, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <VuiBox
      id={id}
      sx={{
        background: "#ffffff",
        border: "1px solid #d8e6f7",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      {/* Clickable header */}
      <VuiBox
        display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap"
        px={2.5} py={1.5} gap={1}
        sx={{ borderBottom: open ? "1px solid rgba(255,255,255,0.06)" : "none" }}
      >
        <VuiBox
          component="button"
          type="button"
          display="flex" alignItems="center" gap={1} flex={1}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setOpen((o) => !o);
            }
          }}
          aria-expanded={open}
          sx={{
            cursor: "pointer",
            userSelect: "none",
            appearance: "none",
            border: 0,
            background: "transparent",
            padding: 0,
            textAlign: "left",
          }}
        >
          <SectionLabel>{title}</SectionLabel>
          {titleChip}
        </VuiBox>
        <VuiBox display="flex" alignItems="center" gap={1}>
          {headerAction}
          <ExpandMoreIcon
            onClick={() => setOpen((o) => !o)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setOpen((o) => !o);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`${open ? "Collapse" : "Expand"} ${title}`}
            aria-expanded={open}
            sx={{
              color: TC.secondary,
              fontSize: 20,
              cursor: "pointer",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.25s ease",
              flexShrink: 0,
            }}
          />
        </VuiBox>
      </VuiBox>
      {/* Collapsible content */}
      <Collapse in={open}>
        <VuiBox p={2.5}>{children}</VuiBox>
      </Collapse>
    </VuiBox>
  );
}

function StatusBadge({ status }) {
  const COLOR = { active: "#4caf50", suspended: "#ff9800", archived: "#9e9e9e", dissolved: "#f44336" };
  const color = COLOR[status] || "#9e9e9e";
  return (
    <Chip
      label={(status || "unknown").toUpperCase()}
      size="small"
      sx={{
        height: 20, fontSize: "0.62rem", fontWeight: FW.bold,
        bgcolor: `${color}22`, color, border: `1px solid ${color}`,
      }}
    />
  );
}

// ── Shared input style ────────────────────────────────────────────────────────

const LABEL_NO_SELECT = {
  shrink: true,
  style: { userSelect: "none", WebkitUserSelect: "none", msUserSelect: "none", pointerEvents: "none" },
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    color: TC.primary,
    fontSize: FS.md,
    background: "#ffffff",
    "& fieldset": { borderColor: "rgba(0,87,216,0.2)" },
    "&:hover fieldset": { borderColor: "rgba(0,87,216,0.4) !important" },
    "&.Mui-focused fieldset": { borderColor: "#0057d8 !important" },
  },
  "& .MuiInputBase-input::placeholder": { color: "rgba(74,96,128,0.5)" },
  "& .MuiInputLabel-root": {
    color: TC.secondary,
    fontSize: "0.78rem",
    backgroundColor: "transparent",
    "&.MuiFormLabel-filled, &.Mui-focused": {
      backgroundColor: "#ffffff",
      paddingLeft: "4px",
      paddingRight: "4px",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: TC.link },
};

function NativeTextarea({ label, value, onChange, placeholder, rows = 3 }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ width: "100%" }}>
      {label && (
        <div style={{ color: TC.secondary, fontSize: FS.sm, marginBottom: 4, userSelect: "none" }}>{label}</div>
      )}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: "100%",
          boxSizing: "border-box",
          background: "#f4f8ff",
          border: `1px solid ${focused ? "#0057d8" : "#d8e6f7"}`,
          borderRadius: "8px",
          color: TC.primary, fontSize: FS.md,
          lineHeight: 1.6,
          padding: "8px 12px",
          resize: "vertical",
          outline: "none",
          fontFamily: "inherit",
          overflowY: "auto",
          display: "block",
          transition: "border-color 0.15s",
          scrollbarWidth: "thin",
          scrollbarColor: "#c0d4ef transparent",
        }}
      />
    </div>
  );
}

function ContactTextField({ label, value, onChange, type = "text", multiline, rows, minRows, maxRows }) {
  if (multiline) {
    return (
      <NativeTextarea
        label={label}
        value={value}
        onChange={onChange}
        rows={minRows || rows}
      />
    );
  }
  return (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      type={type}
      fullWidth
      size="small"
      variant="outlined"
      sx={INPUT_SX}
      InputLabelProps={LABEL_NO_SELECT}
    />
  );
}

// ── Editable Contact Section ──────────────────────────────────────────────────

function EditableContactSection({ profile, tenantId, clientId, onUpdated }) {
  const [editing, setEditing]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [saveMsg, setSaveMsg]   = useState("");
  const [form, setForm]         = useState({});

  // Seed form from profile whenever profile changes
  useEffect(() => {
    setForm({
      contactName:           profile.contactName          || "",
      contactEmail:          profile.contactEmail         || "",
      contactPhone:          profile.contactPhone         || "",
      contactMobile:         profile.contactMobile        || "",
      contactPostalAddress:  profile.contactPostalAddress  || "",
      contactNotes:          profile.contactNotes         || "",
      secondaryContactName:  profile.secondaryContactName  || "",
      secondaryContactEmail: profile.secondaryContactEmail || "",
      secondaryContactPhone: profile.secondaryContactPhone || "",
      secondaryContactPostalAddress: profile.secondaryContactPostalAddress || "",
    });
  }, [profile]);

  const set = (field) => (val) => setForm((f) => ({ ...f, [field]: val }));

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg("");
    try {
      await updateClientProfile(tenantId, clientId, form);
      setSaveMsg("Saved");
      setEditing(false);
      onUpdated?.();
    } catch (err) {
      setSaveMsg(err?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      contactName:           profile.contactName          || "",
      contactEmail:          profile.contactEmail         || "",
      contactPhone:          profile.contactPhone         || "",
      contactMobile:         profile.contactMobile        || "",
      contactPostalAddress:  profile.contactPostalAddress  || "",
      contactNotes:          profile.contactNotes         || "",
      secondaryContactName:  profile.secondaryContactName  || "",
      secondaryContactEmail: profile.secondaryContactEmail || "",
      secondaryContactPhone: profile.secondaryContactPhone || "",
      secondaryContactPostalAddress: profile.secondaryContactPostalAddress || "",
    });
    setEditing(false);
    setSaveMsg("");
  };

  const ViewField = ({ label, value, href }) => {
    if (!value) return null;
    return (
      <Grid item xs={12} sm={6} md={4}>
        <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary, mb: 0.2, fontSize: FS.sm, userSelect: "none" }}>{label}</VuiTypography>
        {href ? (
          <a href={href} style={{ color: TC.link, fontSize: FS.md, textDecoration: "none" }}>{value}</a>
        ) : (
          <VuiTypography variant="button" fontWeight="medium" sx={{ color: TC.primary,  fontSize: FS.md }}>{value}</VuiTypography>
        )}
      </Grid>
    );
  };

  const hasSecondary = profile.secondaryContactName || profile.secondaryContactEmail;

  return (
    <VuiBox>
      {/* Edit/Save controls row */}
      <VuiBox display="flex" alignItems="center" justifyContent="flex-end" mb={editing ? 2 : 1.5} flexWrap="wrap" gap={1}>
        {!editing ? (
          <VuiButton size="small" color="text" variant="outlined" onClick={() => setEditing(true)}
            startIcon={<EditIcon sx={{ fontSize: "13px !important" }} />} sx={{ fontSize: "0.7rem" }}>
            Edit
          </VuiButton>
        ) : (
          <VuiBox display="flex" gap={1}>
            <VuiButton size="small" color="text" variant="outlined" onClick={handleCancel} disabled={saving}
              startIcon={<CloseIcon sx={{ fontSize: "13px !important" }} />} sx={{ fontSize: "0.7rem" }}>
              Cancel
            </VuiButton>
            <VuiButton size="small" color="info" onClick={handleSave} disabled={saving}
              startIcon={saving ? <CircularProgress size={12} color="inherit" /> : <SaveIcon sx={{ fontSize: "13px !important" }} />}
              sx={{ fontSize: "0.7rem" }}>
              {saving ? "Saving…" : "Save"}
            </VuiButton>
          </VuiBox>
        )}
      </VuiBox>

      {saveMsg && (
        <VuiTypography variant="caption" sx={{ color: saveMsg === "Saved" ? "#4caf50" : "#f44336", display: "block", mb: 1, fontSize: FS.sm }}>
          {saveMsg === "Saved" ? "✓ Contact details saved" : `⚠ ${saveMsg}`}
        </VuiTypography>
      )}

      {/* ── View mode ── */}
      {!editing && (
        <VuiBox>
          {/* Primary contact */}
          <VuiBox mb={profile.contactName || profile.contactEmail ? 1.5 : 0}>
            <Grid container spacing={1.5}>
              <ViewField label="Full Name"   value={profile.contactName} />
              <ViewField label="Email"       value={profile.contactEmail}  href={profile.contactEmail ? `mailto:${profile.contactEmail}` : null} />
              <ViewField label="Phone"       value={profile.contactPhone}  href={profile.contactPhone  ? `tel:${profile.contactPhone}` : null} />
              <ViewField label="Mobile"      value={profile.contactMobile} href={profile.contactMobile ? `tel:${profile.contactMobile}` : null} />
              <ViewField label="Postal Address" value={profile.contactPostalAddress} />
            </Grid>
          </VuiBox>
          {profile.contactNotes && (
            <VuiBox mt={1} p={1.5} sx={{ background: "#f4f8ff", borderRadius: "8px", border: "1px solid #d8e6f7" }}>
              <VuiTypography variant="caption" sx={{ color: TC.secondary,  fontSize: FS.sm, lineHeight: 1.6 }}>
                📝 {profile.contactNotes}
              </VuiTypography>
            </VuiBox>
          )}
          {!profile.contactName && !profile.contactEmail && (
            <VuiTypography variant="caption" sx={{ color: TC.tertiary,  opacity: 0.75, fontSize: FS.sm }}>
              No contact details yet — click Edit to add them.
            </VuiTypography>
          )}

          {/* Secondary contact */}
          {hasSecondary && (
            <VuiBox mt={2} pt={2} sx={{ borderTop: "1px solid #e4eef8" }}>
              <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary, textTransform: "uppercase", fontSize: FS.xs, display: "block", mb: 1, userSelect: "none" }}>
                Secondary Contact
              </VuiTypography>
              <Grid container spacing={1.5}>
                <ViewField label="Full Name"  value={profile.secondaryContactName} />
                <ViewField label="Email"      value={profile.secondaryContactEmail} href={profile.secondaryContactEmail ? `mailto:${profile.secondaryContactEmail}` : null} />
                <ViewField label="Phone"      value={profile.secondaryContactPhone} href={profile.secondaryContactPhone ? `tel:${profile.secondaryContactPhone}` : null} />
                <ViewField label="Postal Address" value={profile.secondaryContactPostalAddress} />
              </Grid>
            </VuiBox>
          )}
        </VuiBox>
      )}

      {/* ── Edit mode ── */}
      {editing && (
        <VuiBox>
          {/* Primary contact */}
          <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary, textTransform: "uppercase", fontSize: FS.xs, display: "block", mb: 1.5, userSelect: "none" }}>
            Primary Contact
          </VuiTypography>
          <Grid container spacing={1.5} mb={2}>
            <Grid item xs={12} sm={6}>
              <ContactTextField label="Full Name"   value={form.contactName}      onChange={set("contactName")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ContactTextField label="Email"       value={form.contactEmail}     onChange={set("contactEmail")}     type="email" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ContactTextField label="Phone"       value={form.contactPhone}     onChange={set("contactPhone")}     type="tel" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ContactTextField label="Mobile"      value={form.contactMobile}    onChange={set("contactMobile")}    type="tel" />
            </Grid>
            <Grid item xs={12}>
              <ContactTextField label="Postal Address" value={form.contactPostalAddress} onChange={set("contactPostalAddress")} />
            </Grid>
            <Grid item xs={12}>
              <ContactTextField label="Notes (internal)" value={form.contactNotes} onChange={set("contactNotes")} multiline minRows={2} maxRows={6} />
            </Grid>
          </Grid>

          {/* Secondary contact */}
          <VuiBox pt={2} sx={{ borderTop: "1px solid #e4eef8" }}>
            <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary, textTransform: "uppercase", fontSize: FS.xs, display: "block", mb: 1.5, userSelect: "none" }}>
              Secondary Contact (optional)
            </VuiTypography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <ContactTextField label="Full Name"  value={form.secondaryContactName}     onChange={set("secondaryContactName")} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ContactTextField label="Email"      value={form.secondaryContactEmail}    onChange={set("secondaryContactEmail")}    type="email" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ContactTextField label="Phone"      value={form.secondaryContactPhone}    onChange={set("secondaryContactPhone")}    type="tel" />
              </Grid>
              <Grid item xs={12}>
                <ContactTextField label="Postal Address" value={form.secondaryContactPostalAddress} onChange={set("secondaryContactPostalAddress")} />
              </Grid>
            </Grid>
          </VuiBox>
        </VuiBox>
      )}
    </VuiBox>
  );
}

const URGENCY_CHIP = {
  critical: { color: TC.error, bg: "rgba(244,67,54,0.12)", icon: "🚨" },
  high:     { color: TC.warning, bg: "rgba(255,152,0,0.12)",  icon: "⚠️" },
  medium:   { color: "#ffc107", bg: "rgba(255,193,7,0.10)",  icon: "⏰" },
  standard: { color: TC.success, bg: "rgba(76,175,80,0.10)",  icon: "📋" },
};

function RecordRequestModal({ open, onClose, tenantId, clientId, profile }) {
  const [step, setStep]               = useState("idle"); // idle | previewing | preview | sending | sent | error
  const [preview, setPreview]         = useState(null);
  const [customNote, setCustomNote]   = useState("");
  const [deselected, setDeselected]   = useState(new Set());
  const [errorMsg, setErrorMsg]       = useState("");

  const handlePreview = async () => {
    setStep("previewing");
    setErrorMsg("");
    try {
      const result = await previewRecordRequest(tenantId, clientId, {
        customNote: customNote.trim() || undefined,
      });
      setPreview(result);
      // Build deselected from non-required items that the user might deselect
      setDeselected(new Set());
      setStep("preview");
    } catch (err) {
      setErrorMsg(err?.message || "Failed to generate preview.");
      setStep("error");
    }
  };

  const handleSend = async () => {
    setStep("sending");
    setErrorMsg("");
    try {
      const selectedItemIds = preview.checklist
        .filter((item) => !deselected.has(item.id))
        .map((item) => item.id);
      await sendRecordRequest(tenantId, clientId, {
        customNote: customNote.trim() || undefined,
        selectedItemIds,
      });
      setStep("sent");
    } catch (err) {
      setErrorMsg(err?.message || "Failed to send request.");
      setStep("error");
    }
  };

  const handleClose = () => {
    setStep("idle");
    setPreview(null);
    setCustomNote("");
    setDeselected(new Set());
    setErrorMsg("");
    onClose();
  };

  const toggleItem = (id) => {
    setDeselected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const uc = preview ? URGENCY_CHIP[preview.urgency?.level] || URGENCY_CHIP.standard : URGENCY_CHIP.standard;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: "#1a1f4e",
          border: "1px solid #d8e6f7",
          borderRadius: "16px",
          color: TC.primary,
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: "1px solid #e4eef8", pb: 2 }}>
        <VuiBox display="flex" alignItems="center" gap={1.5}>
          <EmailIcon sx={{ color: TC.linkHover, fontSize: 22 }} />
          <VuiTypography variant="h6" fontWeight="bold" sx={{ color: TC.primary }}>
            AI Record Request
          </VuiTypography>
        </VuiBox>
        {preview && (
          <VuiTypography variant="caption" display="block" mt={0.5} sx={{ color: TC.secondary }}>
            {profile?.legalName} · {preview.industry?.label}
          </VuiTypography>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        {/* ── Step: idle / compose ── */}
        {(step === "idle" || step === "previewing") && (
          <VuiBox>
            <VuiTypography variant="caption" display="block" mb={2} sx={{ color: TC.secondary,  lineHeight: 1.7 }}>
              The AI will generate a document request email tailored to this client's industry,
              accounts deadline, and business structure. You can preview and customise it before sending.
            </VuiTypography>
            {profile?.contactEmail ? (
              <VuiBox
                p={1.5}
                mb={2}
                sx={{ background: "rgba(0,117,255,0.08)", border: "1px solid rgba(0,117,255,0.2)", borderRadius: "10px" }}
              >
                <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                  📧 Will be sent to: <strong style={{ color: "#fff" }}>{profile.contactEmail}</strong>
                </VuiTypography>
              </VuiBox>
            ) : (
              <VuiBox
                p={1.5}
                mb={2}
                sx={{ background: "rgba(244,67,54,0.08)", border: "1px solid rgba(244,67,54,0.25)", borderRadius: "10px" }}
              >
                <VuiTypography variant="caption" sx={{ color: TC.error }}>
                  ⚠ No contact email on file. Please update the client profile before sending.
                </VuiTypography>
              </VuiBox>
            )}
            <NativeTextarea
              label="Add a personal note (optional)"
              placeholder="e.g. Please prioritise bank statements as we're approaching the deadline…"
              rows={3}
              value={customNote}
              onChange={(v) => setCustomNote(v)}
            />
          </VuiBox>
        )}

        {/* ── Step: preview ── */}
        {(step === "preview" || step === "sending") && preview && (
          <VuiBox>
            {/* Urgency banner */}
            {preview.urgency?.level !== "standard" && (
              <VuiBox
                p={1.5}
                mb={2}
                sx={{ background: uc.bg, border: `1px solid ${uc.color}44`, borderRadius: "10px" }}
              >
                <VuiTypography variant="caption" sx={{ color: uc.color, fontWeight: FW.bold }}>
                  {uc.icon} &nbsp;{preview.urgency.label}
                </VuiTypography>
              </VuiBox>
            )}

            {/* Opening paragraph */}
            <VuiBox
              p={2}
              mb={2}
              sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7", borderRadius: "10px" }}
            >
              <VuiTypography variant="caption" fontWeight="bold"
                sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: "0.62rem", display: "block", mb: 1 }}>
                📝 Opening Paragraph (AI-generated)
              </VuiTypography>
              <VuiTypography variant="caption" sx={{ color: TC.secondary,  lineHeight: 1.75, fontSize: "0.8rem" }}>
                {preview.openingParagraph}
              </VuiTypography>
            </VuiBox>

            {/* Checklist */}
            <VuiTypography variant="caption" fontWeight="bold"
              sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: "0.62rem", display: "block", mb: 1 }}>
              📋 Documents to Request ({preview.checklist.length - deselected.size} selected)
            </VuiTypography>
            <VuiBox
              sx={{
                background: "#f4f8ff",
                border: "1px solid #d8e6f7",
                borderRadius: "10px",
                overflow: "hidden",
                mb: 2,
              }}
            >
              {preview.checklist.map((item, idx) => (
                <VuiBox
                  key={item.id}
                  display="flex"
                  alignItems="flex-start"
                  gap={1}
                  p={1.5}
                  sx={{
                    borderBottom: idx < preview.checklist.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
                    opacity: deselected.has(item.id) ? 0.4 : 1,
                    cursor: "pointer",
                    "&:hover": { background: "#f4f8ff" },
                  }}
                  onClick={() => toggleItem(item.id)}
                >
                  <Checkbox
                    checked={!deselected.has(item.id)}
                    size="small"
                    sx={{ color: TC.secondary, "&.Mui-checked": { color: TC.linkHover }, p: 0, mt: "2px" }}
                    onChange={() => {}}
                  />
                  <VuiBox>
                    <VuiTypography variant="caption" fontWeight="medium" display="block" sx={{ color: TC.primary }}>
                      {item.label}
                    </VuiTypography>
                    <VuiTypography variant="caption" sx={{ color: TC.secondary,  fontSize: FS.sm, lineHeight: 1.5 }}>
                      {item.detail}
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>
              ))}
            </VuiBox>

            {customNote && (
              <VuiBox
                p={1.5}
                sx={{ background: "rgba(0,117,255,0.08)", border: "1px solid rgba(0,117,255,0.2)", borderRadius: "10px" }}
              >
                <VuiTypography variant="caption" fontWeight="bold"
                  sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: "0.62rem", display: "block", mb: 0.5 }}>
                  💬 Your custom note
                </VuiTypography>
                <VuiTypography variant="caption" sx={{ color: TC.secondary,  fontSize: "0.8rem" }}>
                  {customNote}
                </VuiTypography>
              </VuiBox>
            )}
          </VuiBox>
        )}

        {/* ── Step: sent ── */}
        {step === "sent" && (
          <VuiBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={3}
            gap={1.5}
          >
            <VuiTypography variant="h2">✅</VuiTypography>
            <VuiTypography variant="h6" fontWeight="bold" sx={{ color: TC.primary }}>Email Sent Successfully</VuiTypography>
            <VuiTypography variant="caption" textAlign="center" sx={{ color: TC.secondary }}>
              The record request has been sent to <strong style={{ color: TC.primary }}>{profile?.contactEmail}</strong>
              {" "}and saved to the request history.
            </VuiTypography>
          </VuiBox>
        )}

        {/* ── Step: error ── */}
        {step === "error" && (
          <VuiBox p={2} sx={{ background: "rgba(244,67,54,0.08)", border: "1px solid rgba(244,67,54,0.25)", borderRadius: "10px" }}>
            <VuiTypography variant="caption" sx={{ color: TC.error }}>
              ❌ {errorMsg}
            </VuiTypography>
          </VuiBox>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: "1px solid #e4eef8", p: 2, gap: 1 }}>
        {step === "sent" ? (
          <VuiButton onClick={handleClose} color="info" size="small">Close</VuiButton>
        ) : step === "preview" ? (
          <>
            <VuiButton onClick={() => setStep("idle")} color="text" size="small" variant="outlined">← Back</VuiButton>
            <VuiButton
              onClick={handleSend}
              color="info"
              size="small"
              disabled={!profile?.contactEmail || deselected.size === preview?.checklist?.length}
              startIcon={<SendIcon fontSize="small" />}
            >
              Send Email
            </VuiButton>
          </>
        ) : step === "error" ? (
          <>
            <VuiButton onClick={handleClose} color="text" size="small" variant="outlined">Close</VuiButton>
            <VuiButton onClick={() => setStep("idle")} color="info" size="small">Try Again</VuiButton>
          </>
        ) : (
          <>
            <VuiButton onClick={handleClose} color="text" size="small" variant="outlined">Cancel</VuiButton>
            <VuiButton
              onClick={handlePreview}
              color="info"
              size="small"
              disabled={step === "previewing" || !profile?.contactEmail}
              startIcon={step === "previewing" ? <CircularProgress size={14} color="inherit" /> : <VisibilityIcon fontSize="small" />}
            >
              {step === "previewing" ? "Generating…" : "Preview Email"}
            </VuiButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ─── Services tab helpers ─────────────────────────────────────────────────────

const SERVICE_COLORS = {
  accounts:            "#4caf50",
  corporation_tax:     "#2196f3",
  vat:                 "#9c27b0",
  payroll:             "#ff9800",
  self_assessment:     "#00bcd4",
  company_secretarial: "#607d8b",
  mtd_quarterly:       "#e91e63",
};

function ServiceStatusChip({ status }) {
  const cfg = {
    active:    { color: TC.success, label: "Active" },
    paused:    { color: TC.warning, label: "Paused" },
    cancelled: { color: "#9e9e9e", label: "Cancelled" },
  }[status] || { color: "#9e9e9e", label: status };
  return (
    <Chip label={cfg.label.toUpperCase()} size="small" sx={{
      height: 18, fontSize: "0.6rem", fontWeight: FW.bold,
      bgcolor: `${cfg.color}22`, color: cfg.color, border: `1px solid ${cfg.color}`,
    }} />
  );
}

function CompanyServicesTab({ profile, tenantId, canManage }) {
  const [controller] = useVisionUIController();
  const { serviceDefaults } = controller;

  const [masterServices, setMasterServices] = useState([]);
  const [assignments, setAssignments]       = useState([]);
  const [loading, setLoading]               = useState(true);
  const [assigning, setAssigning]           = useState(false);
  const [assignError, setAssignError]       = useState("");
  const [removeId, setRemoveId]             = useState(null);
  const [pickerOpen, setPickerOpen]         = useState(false);
  const [pendingServiceId, setPendingServiceId] = useState(null);
  const [tenantUsers, setTenantUsers]       = useState([]);
  const [usersLoading, setUsersLoading]     = useState(false);
  const [selectedManager, setSelectedManager] = useState("");

  async function load() {
    setLoading(true);
    try {
      const [ms, as] = await Promise.all([
        listMasterServices(),
        listClientServices(tenantId, profile.id),
      ]);
      setMasterServices(ms.services || []);
      setAssignments(as.assignments || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (tenantId && profile?.id) load(); }, [tenantId, profile?.id]);

  const activeIds = new Set(assignments.filter((a) => a.status === "active").map((a) => a.serviceId));

  async function openPicker(serviceId) {
    setPendingServiceId(serviceId);
    const def = serviceDefaults?.[serviceId];
    setSelectedManager(def?.uid || "");
    setAssignError("");
    setPickerOpen(true);
    if (tenantUsers.length === 0) {
      setUsersLoading(true);
      try {
        const res = await listTenantUsers({ tenantId });
        setTenantUsers(res.users || res || []);
      } catch (_) { setTenantUsers([]); }
      finally { setUsersLoading(false); }
    }
  }

  async function confirmAssign() {
    setAssignError("");
    setAssigning(true);
    const mgr = tenantUsers.find((u) => u.uid === selectedManager);
    try {
      await assignClientService(tenantId, profile.id, {
        serviceId:           pendingServiceId,
        clientName:          profile.legalName || profile.id,
        assignedManagerUid:  mgr?.uid  || null,
        assignedManagerName: mgr?.displayName || mgr?.email || null,
      });
      setPickerOpen(false);
      await load();
    } catch (e) {
      setAssignError(e.message);
    } finally { setAssigning(false); }
  }

  async function handleRemove(assignmentId) {
    setRemoveId(assignmentId);
    try {
      await removeClientService(tenantId, profile.id, assignmentId);
      await load();
    } finally { setRemoveId(null); }
  }

  if (loading) return <VuiBox p={4} display="flex" justifyContent="center"><CircularProgress size={28} /></VuiBox>;

  return (
    <VuiBox display="flex" flexDirection="column" gap={3}>
      {/* Manager picker overlay */}
      {pickerOpen && (
        <VuiBox sx={{
          position: "fixed", inset: 0, zIndex: 1300,
          background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <VuiBox sx={{
            background: "linear-gradient(135deg,#0f1535,#1a1f3a)",
            border: "1px solid #d8e6f7",
            borderRadius: "16px", p: 3, width: 360, display: "flex", flexDirection: "column", gap: 2,
          }}>
            <VuiTypography variant="h6" fontWeight="bold" sx={{ color: TC.primary }}>Assign Service</VuiTypography>
            <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
              {masterServices.find((s) => s.id === pendingServiceId)?.name} → {profile.legalName}
            </VuiTypography>
            <VuiTypography variant="caption" fontWeight="medium" sx={{ color: TC.primary }}>
              Assign Manager (optional)
              {serviceDefaults?.[pendingServiceId] && (
                <span style={{ color: "rgba(247,183,49,0.85)", fontWeight: FW.normal, marginLeft: 6, fontSize: "0.7rem" }}>★ default pre-filled</span>
              )}
            </VuiTypography>
            {usersLoading ? (
              <VuiBox display="flex" justifyContent="center" py={1}><CircularProgress size={22} /></VuiBox>
            ) : (
              <FormControl fullWidth size="small">
                <Select value={selectedManager} onChange={(e) => setSelectedManager(e.target.value)} displayEmpty
                  sx={{ color: TC.primary, fontSize: FS.md, background: "#f4f8ff", border: "1px solid #d8e6f7", borderRadius: "8px", ".MuiSelect-icon": { color: "#fff" } }}>
                  <MenuItem value=""><em style={{ color: "#aaa" }}>No manager</em></MenuItem>
                  {tenantUsers.map((u) => (
                    <MenuItem key={u.uid} value={u.uid}>{u.displayName || u.email}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {assignError && <VuiTypography variant="caption" color="error">{assignError}</VuiTypography>}
            <VuiBox display="flex" gap={1} justifyContent="flex-end" mt={1}>
              <VuiButton size="small" variant="outlined" color="secondary" onClick={() => setPickerOpen(false)} disabled={assigning}>Cancel</VuiButton>
              <VuiButton size="small" variant="contained" color="info" onClick={confirmAssign} disabled={assigning}>
                {assigning ? <CircularProgress size={16} color="inherit" /> : "Confirm"}
              </VuiButton>
            </VuiBox>
          </VuiBox>
        </VuiBox>
      )}

      {/* Available services */}
      <VuiBox p={2} sx={{ background: "#f4f8ff", borderRadius: "12px", border: "1px solid #d8e6f7" }}>
        <VuiTypography variant="caption" fontWeight="bold"
          sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: FS.xs, letterSpacing: "0.08em", display: "block", mb: 1.5 }}>
          Available Services
        </VuiTypography>
        <Grid container spacing={2}>
          {masterServices.map((svc) => {
            const isActive = activeIds.has(svc.id);
            const color    = SERVICE_COLORS[svc.id] || "#0075ff";
            return (
              <Grid key={svc.id} item xs={12} sm={6} md={4}>
                <VuiBox p={1.5} sx={{
                  background: isActive ? `${color}15` : "rgba(255,255,255,0.03)",
                  border: `1px solid ${isActive ? color : "rgba(255,255,255,0.08)"}`,
                  borderRadius: "10px", transition: "all 0.2s",
                  display: "flex", flexDirection: "column", gap: 0.5,
                }}>
                  <VuiBox display="flex" justifyContent="space-between" alignItems="center">
                    <VuiTypography variant="button" fontWeight="medium" sx={{ color: TC.primary,  fontSize: FS.md }}>{svc.name}</VuiTypography>
                    <Chip label={svc.frequency} size="small" sx={{ height: 16, fontSize: "0.58rem", fontWeight: FW.semibold, bgcolor: `${color}22`, color, border: `1px solid ${color}44` }} />
                  </VuiBox>
                  <VuiTypography variant="caption" sx={{ color: TC.secondary,  mb: 1 }}>{svc.description}</VuiTypography>
                  {canManage && (
                    isActive
                      ? <VuiTypography variant="caption" sx={{ color: TC.success, fontWeight: FW.bold }}>✓ Assigned</VuiTypography>
                      : (
                        <VuiButton size="small" variant="outlined" color="info"
                          onClick={() => openPicker(svc.id)} disabled={assigning}
                          sx={{ py: 0.3, fontSize: FS.sm, mt: 0.5 }}>
                          + Assign
                        </VuiButton>
                      )
                  )}
                </VuiBox>
              </Grid>
            );
          })}
        </Grid>
      </VuiBox>

      {/* Assigned services */}
      {assignments.length > 0 && (
        <VuiBox p={2} sx={{ background: "#f4f8ff", borderRadius: "12px", border: "1px solid #d8e6f7" }}>
          <VuiTypography variant="caption" fontWeight="bold"
            sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: FS.xs, letterSpacing: "0.08em", display: "block", mb: 1.5 }}>
            Assigned Services
          </VuiTypography>
          <VuiBox display="flex" flexDirection="column" gap={1.5}>
            {assignments.map((a) => {
              const color = SERVICE_COLORS[a.serviceId] || "#0075ff";
              return (
                <VuiBox key={a.id} p={1.5} sx={{
                  background: "#f4f8ff",
                  border: "1px solid #d8e6f7",
                  borderLeft: `3px solid ${color}`,
                  borderRadius: "8px",
                  display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1,
                }}>
                  <VuiBox display="flex" alignItems="center" gap={1.5}>
                    <VuiTypography variant="button" fontWeight="bold" sx={{ color: TC.primary,  fontSize: "0.83rem" }}>{a.serviceName}</VuiTypography>
                    <ServiceStatusChip status={a.status} />
                    <VuiTypography variant="caption" sx={{ color: TC.secondary }}>Since {a.startDate}</VuiTypography>
                    {a.assignedManagerName && (
                      <Chip label={`👤 ${a.assignedManagerName}`} size="small"
                        sx={{ height: 18, fontSize: FS.xs, bgcolor: "#7a94b0", color: "#ccc" }} />
                    )}
                  </VuiBox>
                  {canManage && a.status !== "cancelled" && (
                    <VuiButton size="small" variant="outlined" color="error"
                      onClick={() => handleRemove(a.id)} disabled={removeId === a.id}
                      sx={{ py: 0.3, fontSize: "0.7rem" }}>
                      {removeId === a.id ? "Removing…" : "Remove"}
                    </VuiButton>
                  )}
                </VuiBox>
              );
            })}
          </VuiBox>
        </VuiBox>
      )}
    </VuiBox>
  );
}

// ─── AD01 task actions ────────────────────────────────────────────────────────

const AD01_TASK_CFG = {
  "Confirm new registered address with client": {
    type: "email", icon: "📧", label: "Email Client",
    templateKey: "ad01_confirm_address",
    subject: (p) => `Confirming your new registered address — ${p?.legalName || ""}`,
    body:    (p) => `Dear Client,\n\nWe are writing to confirm the new registered address for ${p?.legalName || "your company"}.\n\nNew address: ${p?.registeredAddress || "(please advise)"}\n\nCould you please confirm this is correct so we can proceed with filing form AD01 with Companies House?\n\nKind regards`,
  },
  "Prepare form AD01": {
    type: "none",
  },
  "File AD01 with Companies House": {
    type: "wizard", icon: "🏛", label: "File In-App",
  },
  "Confirm address updated on CH register": {
    type: "ch_register", icon: "🔍", label: "Check Register",
  },
  "Re-sync client from Companies House": {
    type: "resync", icon: "🔄", label: "Re-sync",
  },
  "Update records and notify client": {
    type: "email", icon: "📧", label: "Notify Client",
    templateKey: "ad01_update_records",
    subject: (p) => `Address change filed — ${p?.legalName || ""}`,
    body:    (p) => `Dear Client,\n\nWe are pleased to confirm that the registered address change (AD01) for ${p?.legalName || "your company"} has been filed with Companies House and our records have been updated.\n\nNew registered address: ${p?.registeredAddress || "(updated on CH register)"}\n\nPlease let us know if you have any questions.\n\nKind regards`,
  },
};

function Ad01TaskRow({ task, tenantId, clientId, profile, onToggle, updating }) {
  const cfg = AD01_TASK_CFG[task.title];
  const emailDialogTitleId = `email-dialog-title-${task.id}`;
  const emailDialogDescriptionId = `email-dialog-description-${task.id}`;
  const [emailOpen,    setEmailOpen]    = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody,    setEmailBody]    = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailMsg,     setEmailMsg]     = useState("");
  const [syncing,      setSyncing]      = useState(false);
  const [syncMsg,      setSyncMsg]      = useState("");
  const [wizardOpen,   setWizardOpen]   = useState(false);

  const openEmail = async () => {
    // Load saved template, fall back to hardcoded default
    let subject = cfg.subject?.(profile) || "";
    let body    = cfg.body?.(profile) || "";
    try {
      const templates = await getEmailTemplates(tenantId);
      const tpl = templates?.[cfg.templateKey];
      if (tpl?.subject) subject = tpl.subject.replace(/\{\{company\}\}/g, profile?.legalName || "");
      if (tpl?.body)    body    = tpl.body.replace(/\{\{company\}\}/g, profile?.legalName || "").replace(/\{\{address\}\}/g, profile?.registeredAddress || "(please advise)");
    } catch (_) { /* use defaults */ }
    setEmailSubject(subject);
    setEmailBody(body);
    setEmailMsg("");
    setEmailOpen(true);
  };

  const handleSendEmail = async () => {
    setEmailSending(true); setEmailMsg("");
    try {
      await sendClientEmail(tenantId, clientId, {
        subject: emailSubject,
        bodyText: emailBody,
        tag: `AD01 — ${task.title}`,
      });
      setEmailMsg("✓ Email sent");
      if (task.status !== "done") onToggle(task);
      setTimeout(() => { setEmailOpen(false); setEmailMsg(""); }, 1500);
    } catch (err) {
      setEmailMsg(err?.message || "Failed to send.");
    } finally { setEmailSending(false); }
  };

  const handleResync = async () => {
    setSyncing(true); setSyncMsg("");
    try {
      await resyncClientFromCH(tenantId, clientId);
      setSyncMsg("✓ Synced");
      if (task.status !== "done") onToggle(task);
      setTimeout(() => setSyncMsg(""), 3000);
    } catch (err) {
      setSyncMsg(err?.message || "Sync failed.");
    } finally { setSyncing(false); }
  };

  const ActionBtn = () => {
    if (!cfg || cfg.type === "none") return null;
    if (cfg.type === "link") return (
      <VuiButton size="small" variant="outlined" color="info"
        onClick={(e) => {
          e.stopPropagation();
          window.open(cfg.url, "_blank");
          if (task.status !== "done") onToggle(task);
        }}
        sx={{ fontSize: "0.63rem", py: 0.2, px: 0.9, minWidth: 0, whiteSpace: "nowrap" }}>
        {cfg.icon} {cfg.label}
      </VuiButton>
    );
    if (cfg.type === "ch_register") return (
      <VuiButton size="small" variant="outlined" color="info"
        disabled={!profile?.companyRegNumber}
        onClick={(e) => {
          e.stopPropagation();
          window.open(`https://find-and-update.company-information.service.gov.uk/company/${profile.companyRegNumber}`, "_blank");
          if (task.status !== "done") onToggle(task);
        }}
        sx={{ fontSize: "0.63rem", py: 0.2, px: 0.9, minWidth: 0, whiteSpace: "nowrap" }}>
        🔍 Check Register
      </VuiButton>
    );
    if (cfg.type === "resync") return (
      <VuiBox display="flex" alignItems="center" gap={0.8}>
        <VuiButton size="small" variant="outlined" color="info" disabled={syncing}
          onClick={(e) => { e.stopPropagation(); handleResync(); }}
          sx={{ fontSize: "0.63rem", py: 0.2, px: 0.9, minWidth: 0, whiteSpace: "nowrap" }}>
          {syncing ? <CircularProgress size={10} sx={{ color: "inherit" }} /> : "🔄"} Re-sync
        </VuiButton>
        {syncMsg && (
          <VuiTypography variant="caption" sx={{ fontSize: "0.63rem", color: syncMsg.startsWith("✓") ? TC.success : TC.error }}>
            {syncMsg}
          </VuiTypography>
        )}
      </VuiBox>
    );
    if (cfg.type === "email") return (
      <>
        <VuiButton size="small" variant="outlined" color="info"
          onClick={(e) => { e.stopPropagation(); openEmail(); }}
          sx={{ fontSize: "0.63rem", py: 0.2, px: 0.9, minWidth: 0, whiteSpace: "nowrap" }}>
          📧 {cfg.label}
        </VuiButton>
        <Dialog open={emailOpen} onClose={() => !emailSending && setEmailOpen(false)}
          maxWidth="lg" fullWidth
          aria-labelledby={emailDialogTitleId}
          aria-describedby={emailDialogDescriptionId}
          PaperProps={{ sx: { borderRadius: "16px", border: "1px solid #d8e6f7", background: "#fff", minHeight: 520, width: "100%" } }}>
          <DialogTitle id={emailDialogTitleId} sx={{ pb: 1, pt: 2.5, px: 4, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #eef2f7" }}>
            <VuiBox display="flex" alignItems="center" gap={1.2}>
              <VuiBox sx={{ width: 36, height: 36, borderRadius: "10px", background: "linear-gradient(135deg,#e8f0fe,#c2d7fb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem" }}>
                📧
              </VuiBox>
              <VuiBox>
                <VuiTypography sx={{ fontWeight: FW.bold, fontSize: "1rem", color: TC.primary, lineHeight: 1.2 }}>
                  {cfg.label}
                </VuiTypography>
                {profile?.email && (
                  <VuiTypography sx={{ fontSize: "0.72rem", color: TC.tertiary }}>
                    To: {profile.email}
                  </VuiTypography>
                )}
              </VuiBox>
            </VuiBox>
            <IconButton size="small" onClick={() => setEmailOpen(false)} disabled={emailSending} aria-label="Close email dialog">
              <CloseIcon sx={{ fontSize: "1.1rem" }} />
            </IconButton>
          </DialogTitle>
          <DialogContent id={emailDialogDescriptionId} sx={{ pt: 3, px: 4, pb: 1 }}>
            <div style={{ width: "100%", boxSizing: "border-box", paddingTop: 4 }}>
              <TextField fullWidth size="medium" label="Subject" value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                InputLabelProps={LABEL_NO_SELECT}
                style={{ width: "100%", display: "block", marginBottom: 20 }}
                sx={{ "& .MuiInputBase-root": { fontSize: "0.9rem" }, "& input": { width: "100% !important" } }} />
              <TextField fullWidth multiline rows={11} label="Message" value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                InputLabelProps={LABEL_NO_SELECT}
                placeholder="Write your message here…"
                style={{ width: "100%", display: "block" }}
                sx={{ "& .MuiInputBase-root": { fontSize: "0.88rem", lineHeight: 1.6 }, "& input": { width: "100% !important" }, "& textarea": { width: "100% !important" } }} />
              {emailMsg && (
                <VuiTypography variant="caption" sx={{ color: emailMsg.startsWith("✓") ? TC.success : TC.error, fontSize: "0.78rem", display: "block", mt: 1 }}>
                  {emailMsg}
                </VuiTypography>
              )}
            </div>
          </DialogContent>
          <DialogActions sx={{ px: 4, py: 2, gap: 1.2, borderTop: "1px solid #eef2f7" }}>
            <VuiButton variant="outlined" color="secondary"
              onClick={() => setEmailOpen(false)} disabled={emailSending}
              sx={{ px: 3 }}>
              Cancel
            </VuiButton>
            <VuiButton color="info" variant="contained"
              onClick={handleSendEmail}
              disabled={emailSending || !emailSubject.trim() || !emailBody.trim()}
              startIcon={emailSending ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
              sx={{ px: 3 }}>
              {emailSending ? "Sending…" : "Send Email"}
            </VuiButton>
          </DialogActions>
        </Dialog>
      </>
    );
    return null;
  };

  return (
    <>
      <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={1}
        sx={{ p: 0.8, borderRadius: "6px", opacity: updating === task.id ? 0.5 : 1, "&:hover": { background: "rgba(0,117,255,0.04)" } }}>
        {/* Checkbox + title */}
        <VuiBox component="button" type="button" display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}
          onClick={() => onToggle(task)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onToggle(task);
            }
          }}
          aria-pressed={task.status === "done"}
          sx={{ cursor: "pointer", appearance: "none", border: 0, background: "transparent", padding: 0, textAlign: "left" }}>
          <VuiBox sx={{
            width: 16, height: 16, borderRadius: "4px", flexShrink: 0,
            border: task.status === "done" ? "none" : `2px solid ${TC.secondary}66`,
            background: task.status === "done" ? "#4caf50" : "transparent",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {task.status === "done" && <span style={{ color: "#fff", fontSize: "0.6rem", lineHeight: 1 }}>✓</span>}
          </VuiBox>
          <VuiTypography variant="caption" sx={{
            color: task.status === "done" ? TC.tertiary : TC.primary,
            textDecoration: task.status === "done" ? "line-through" : "none",
            fontSize: FS.sm,
          }}>
            {task.title}
          </VuiTypography>
        </VuiBox>
        {/* Action button — wizard type rendered here */}
        <VuiBox flexShrink={0}>
          {cfg?.type === "wizard" ? (
            <VuiButton size="small" variant="contained" color="info"
              onClick={(e) => { e.stopPropagation(); setWizardOpen(true); }}
              sx={{ fontSize: "0.63rem", py: 0.3, px: 1, minWidth: 0, whiteSpace: "nowrap", background: "#0057d8", "&:hover": { background: "#0041a8" } }}>
              🏛 File In-App
            </VuiButton>
          ) : (
            <ActionBtn />
          )}
        </VuiBox>
      </VuiBox>

      {/* AD01 Filing Wizard */}
      <Ad01FilingWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        tenantId={tenantId}
        clientId={clientId}
        profile={profile}
        onFiled={() => {
          setWizardOpen(false);
          if (task.status !== "done") onToggle(task);
        }}
      />
    </>
  );
}

// ─── Jobs & Tasks tab helpers ─────────────────────────────────────────────────

function derivedStatus(job) {
  const { status, completedCount = 0, taskCount = 0, dueDate } = job;
  if (status === "cancelled") return "cancelled";
  if (status === "invoiced")  return "invoiced";
  if (status === "filed")     return "filed";
  if (taskCount > 0 && completedCount >= taskCount) return "filed";
  if (status === "approved")  return "approved";
  if (status === "review")    return "review";
  if (dueDate) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (new Date(dueDate) < today) return "overdue";
  }
  if (completedCount > 0) return "in_progress";
  return "pending";
}

const JOB_STATUS_CFG = {
  pending:     { color: TC.warning, label: "Pending" },
  in_progress: { color: "#2196f3", label: "In Progress" },
  review:      { color: "#9c27b0", label: "Review" },
  approved:    { color: "#00bcd4", label: "Approved" },
  filed:       { color: TC.success, label: "Filed" },
  invoiced:    { color: TC.success, label: "Invoiced" },
  cancelled:   { color: "#9e9e9e", label: "Cancelled" },
  overdue:     { color: TC.error,  label: "Overdue" },
};

function CompanyJobCard({ job, tenantId, clientId, profile, compact = false }) {
  const [expanded, setExpanded]     = useState(false);
  const [tasks, setTasks]           = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [updatingTask, setUpdatingTask] = useState(null);
  const [jobStatus, setJobStatus]   = useState(derivedStatus(job));
  const [savingStatus, setSavingStatus] = useState(false);

  async function handleStatusChange(e) {
    const newStatus = e.target.value;
    setSavingStatus(true);
    try {
      await updateClientJob(tenantId, job.id, { status: newStatus });
      setJobStatus(newStatus);
    } finally {
      setSavingStatus(false);
    }
  }

  async function loadTasks() {
    if (tasks.length) { setExpanded(!expanded); return; }
    setExpanded(true);
    setTasksLoading(true);
    try {
      const res = await listJobTasks(tenantId, job.id);
      setTasks(res.tasks || []);
    } finally { setTasksLoading(false); }
  }

  async function toggleTask(task) {
    const next = task.status === "done" ? "pending" : "done";
    setUpdatingTask(task.id);
    try {
      await updateJobTask(tenantId, job.id, task.id, { status: next });
      setTasks((prev) => prev.map((t) => t.id === task.id ? { ...t, status: next } : t));
    } finally { setUpdatingTask(null); }
  }

  const cfg       = JOB_STATUS_CFG[jobStatus] || JOB_STATUS_CFG.pending;
  const pct       = job.taskCount ? Math.round((job.completedCount / job.taskCount) * 100) : 0;
  const svcColor  = SERVICE_COLORS[job.serviceId] || "#0075ff";
  const isOverdue = job.dueDate && new Date(job.dueDate) < new Date() && jobStatus !== "filed";

  return (
    <VuiBox sx={{
      background: "#f4f8ff",
      border: "1px solid #d8e6f7",
      borderLeft: `3px solid ${svcColor}`,
      borderRadius: "10px", overflow: "hidden",
    }}>
      <VuiBox p={1.5} display="flex" alignItems="center" justifyContent="space-between"
        onClick={compact ? undefined : loadTasks}
        sx={{ cursor: compact ? "default" : "pointer", "&:hover": { background: compact ? "transparent" : "rgba(255,255,255,0.03)" } }}>
        <VuiBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <VuiTypography variant="button" sx={{ color: TC.primary, fontSize: FS.md, fontWeight: FW.bold }}>{job.title}</VuiTypography>
          {savingStatus
            ? <CircularProgress size={14} sx={{ color: cfg.color }} />
            : (
              <Select
                value={jobStatus}
                onChange={handleStatusChange}
                size="small"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  color: cfg.color, fontSize: FS.xs, fontWeight: FW.bold,
                  background: `${cfg.color}18`, border: `1px solid ${cfg.color}55`,
                  borderRadius: "6px", height: 22,
                  ".MuiSelect-icon": { color: cfg.color, fontSize: "0.9rem" },
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              >
                {Object.entries(JOB_STATUS_CFG).map(([k, v]) => (
                  <MenuItem key={k} value={k} sx={{ fontSize: FS.sm }}>{v.label}</MenuItem>
                ))}
              </Select>
            )
          }
          {isOverdue && (
            <Chip label="OVERDUE" size="small" sx={{ height: 18, fontSize: "0.6rem", fontWeight: FW.bold, bgcolor: "rgba(244,67,54,0.15)", color: TC.error, border: "1px solid #f44336" }} />
          )}
        </VuiBox>
        <VuiBox display="flex" alignItems="center" gap={2}>
          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm }}>
            Due: <strong style={{ color: isOverdue ? TC.error : TC.primary }}>{job.dueDate}</strong>
          </VuiTypography>
          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm }}>{pct}%</VuiTypography>
          {!compact && <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm }}>{expanded ? "▲" : "▼"}</VuiTypography>}
        </VuiBox>
      </VuiBox>
      <VuiBox sx={{ height: 3, background: "#f4f8ff" }}>
        <VuiBox sx={{ height: 3, width: `${pct}%`, background: svcColor, transition: "width 0.3s" }} />
      </VuiBox>
      {!compact && expanded && (
        <VuiBox p={1.5} pt={1} display="flex" flexDirection="column" gap={0.5}>
          {tasksLoading ? (
            <VuiBox display="flex" justifyContent="center" py={1}><CircularProgress size={20} /></VuiBox>
          ) : (
            tasks.map((task) => (
              job.serviceId === "ad01_address_change" ? (
                <Ad01TaskRow
                  key={task.id}
                  task={task}
                  tenantId={tenantId}
                  clientId={clientId}
                  profile={profile}
                  onToggle={toggleTask}
                  updating={updatingTask}
                />
              ) : (
                <VuiBox key={task.id} display="flex" alignItems="center" gap={1.5}
                  onClick={() => toggleTask(task)}
                  sx={{ cursor: "pointer", opacity: updatingTask === task.id ? 0.5 : 1, p: 0.8, borderRadius: "6px", "&:hover": { background: "#f4f8ff" } }}>
                  <VuiBox sx={{
                    width: 16, height: 16, borderRadius: "4px", flexShrink: 0,
                    border: task.status === "done" ? "none" : `2px solid ${TC.secondary}66`,
                    background: task.status === "done" ? "#4caf50" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {task.status === "done" && <span style={{ color: TC.primary, fontSize: FS.xs, lineHeight: 1 }}>✓</span>}
                  </VuiBox>
                  <VuiTypography variant="caption"
                    sx={{
                      color: task.status === "done" ? TC.tertiary : TC.primary,
                      textDecoration: task.status === "done" ? "line-through" : "none",
                      fontSize: FS.sm,
                    }}>
                    {task.title}
                  </VuiTypography>
                </VuiBox>
              )
            ))
          )}
        </VuiBox>
      )}
    </VuiBox>
  );
}

const JOB_SERVICE_TABS = [
  { label: "PAYE",                  serviceId: "payroll"             },
  { label: "VAT",                   serviceId: "vat"                 },
  { label: "Accounts Prep",         serviceId: "accounts"            },
  { label: "CT600",                 serviceId: "corporation_tax"     },
  { label: "Confirmation Statement",serviceId: "company_secretarial" },
  { label: "Address Change (AD01)", serviceId: "ad01_address_change" },
];

const AD01_TAB_INDEX = 5;

function ServiceJobsPane({ jobs, loading, tenantId, profile, compact = false }) {
  if (loading) return <VuiBox p={4} display="flex" justifyContent="center"><CircularProgress size={28} /></VuiBox>;

  if (jobs.length === 0) return (
    <VuiBox px={2} py={3} textAlign="center">
      <VuiTypography variant="caption" sx={{ color: TC.secondary }}>No jobs found for this service.</VuiTypography>
    </VuiBox>
  );

  const grouped = jobs.reduce((acc, job) => {
    const key = derivedStatus(job) === "filed" || derivedStatus(job) === "invoiced" ? "Completed" : "Active";
    if (!acc[key]) acc[key] = [];
    acc[key].push(job);
    return acc;
  }, {});

  return (
    <VuiBox display="flex" flexDirection="column" gap={3}>
      {["Active", "Completed"].filter((k) => grouped[k]?.length).map((key) => (
        <VuiBox key={key}>
          <VuiTypography variant="caption" fontWeight="bold"
            sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: FS.xs, letterSpacing: "0.08em", display: "block", mb: 1.5 }}>
            {key} Jobs ({grouped[key].length})
          </VuiTypography>
          <VuiBox display="flex" flexDirection="column" gap={1.5}>
            {grouped[key].map((job) => (
              <CompanyJobCard key={job.id} job={job} tenantId={tenantId} clientId={profile?.id} profile={profile} compact={compact} />
            ))}
          </VuiBox>
        </VuiBox>
      ))}
    </VuiBox>
  );
}

function CompanyJobsTab({ profile, tenantId, directors = [], onProfileUpdated, onDirectorsUpdated, initialJobTab = 0 }) {
  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobTab, setJobTab]   = useState(initialJobTab);
  const history = useHistory();

  // ── New AD01 Job dialog state ────────────────────────────
  const [newJobOpen,    setNewJobOpen]    = useState(false);
  const [newJobDueDate, setNewJobDueDate] = useState("");
  const [newJobNotes,   setNewJobNotes]   = useState("");
  const [newJobSaving,  setNewJobSaving]  = useState(false);
  const [newJobError,   setNewJobError]   = useState("");

  const handleCreateAd01Job = async () => {
    setNewJobSaving(true);
    setNewJobError("");
    try {
      const now = Date.now();
      const periodKey = `ad01-${now}`;
      const dateLabel = newJobDueDate
        ? new Date(newJobDueDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
        : new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
      const payload = {
        title:      `Address Change (AD01) — ${dateLabel}`,
        jobType:    "ad01_address_change",
        periodKey,
        dueDate:    newJobDueDate || null,
        notes:      newJobNotes.trim(),
        clientName: profile?.legalName || profile?.id,
        tasks:      [], // backend will seed from TASK_TEMPLATES["ad01_address_change"]
      };
      const result = await createClientJob(tenantId, profile.id, payload);
      setJobs((prev) => [result.job, ...prev]);
      setNewJobOpen(false);
      setNewJobDueDate("");
      setNewJobNotes("");
    } catch (err) {
      setNewJobError(err?.message || "Failed to create job. Please try again.");
    } finally {
      setNewJobSaving(false);
    }
  };

  useEffect(() => {
    if (!tenantId || !profile?.id) return;
    setLoading(true);
    listClientJobs(tenantId, profile.id)
      .then((res) => setJobs(res.jobs || []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, [tenantId, profile?.id]);

  const activeServiceId = JOB_SERVICE_TABS[jobTab].serviceId;
  const filtered = jobs.filter((j) => j.serviceId === activeServiceId);
  const isCSTab       = jobTab === 4;
  const isAD01Tab     = jobTab === AD01_TAB_INDEX;
  const isAccountsTab = jobTab === 2; // "Accounts Prep"

  return (
    <VuiBox>
      <Tabs
        value={jobTab}
        onChange={(_, v) => setJobTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          mb: 2,
          minHeight: 34,
          "& .MuiTabs-indicator": { backgroundColor: "#0075ff" },
          "& .MuiTab-root": { color: TC.secondary, fontSize: FS.sm, minHeight: 34, py: 0.5, textTransform: "none", px: 1.5 },
          "& .Mui-selected": { color: "#0a1628 !important", fontWeight: "700 !important" },
          "& .MuiTabScrollButton-root": { color: TC.secondary, opacity: 1 },
        }}
      >
        {JOB_SERVICE_TABS.map((t, i) => {
          const count = jobs.filter((j) => j.serviceId === t.serviceId).length;
          return <Tab key={i} label={count > 0 ? `${t.label} (${count})` : t.label} />;
        })}
      </Tabs>

      {/* ── Accounts Prep: guided workflow banner ── */}
      {isAccountsTab && (
        <VuiBox
          mb={2} p={1.5}
          sx={{
            borderRadius: "10px",
            background: "rgba(76,175,80,0.05)",
            border: "1px solid rgba(76,175,80,0.22)",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap",
          }}
        >
          <VuiBox>
            <VuiTypography variant="caption" sx={{ color: TC.primary, fontWeight: FW.bold, fontSize: FS.md, display: "block" }}>
              📋 Accounts Preparation Workflow
            </VuiTypography>
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm }}>
              Follow the 8-step guided workflow to prepare, review, approve and file annual accounts.
            </VuiTypography>
          </VuiBox>
          <VuiButton
            size="small"
            color="success"
            variant="contained"
            onClick={() => history.push(`/accounts-prep/${profile?.id}`)}
            sx={{ fontSize: FS.sm, whiteSpace: "nowrap", background: "#4caf50", color: "#fff", "&:hover": { background: "#388e3c" } }}
          >
            🚀 Open Workflow
          </VuiButton>
        </VuiBox>
      )}

      {isAD01Tab && (
        <>
          <VuiBox mb={2} p={1.5} sx={{
            borderRadius: "10px",
            background: "rgba(0,117,255,0.05)",
            border: "1px solid rgba(0,117,255,0.2)",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap",
          }}>
            <VuiBox>
              <VuiTypography variant="caption" sx={{ color: TC.primary, fontWeight: FW.bold, fontSize: FS.md, display: "block" }}>
                🏠 Address Change (AD01) Jobs
              </VuiTypography>
              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm }}>
                Track registered address change filings for this client. All jobs also appear on the central Jobs Board.
              </VuiTypography>
            </VuiBox>
            <VuiBox display="flex" gap={1} flexWrap="wrap">
              <VuiButton
                size="small"
                color="info"
                variant="contained"
                onClick={() => setNewJobOpen(true)}
                sx={{ fontSize: FS.sm, whiteSpace: "nowrap", background: "#0075ff", color: "#fff", "&:hover": { background: "#0057d8" } }}
              >
                + New Address Change
              </VuiButton>
              <VuiButton
                size="small"
                color="info"
                variant="outlined"
                onClick={() => history.push("/jobs-board")}
                endIcon={<OpenInNewIcon sx={{ fontSize: "13px !important" }} />}
                sx={{ fontSize: FS.sm, whiteSpace: "nowrap", borderColor: "rgba(0,117,255,0.4)", color: TC.link, "&:hover": { borderColor: "#0075ff", background: "rgba(0,117,255,0.08)" } }}
              >
                View Jobs Board
              </VuiButton>
            </VuiBox>
          </VuiBox>

          {/* ── New AD01 Job Dialog ── */}
          <Dialog open={newJobOpen} onClose={() => !newJobSaving && setNewJobOpen(false)} maxWidth="sm" fullWidth
            PaperProps={{ sx: { background: "#fff", border: "1px solid #d8e6f7", borderRadius: "16px" } }}>
            <DialogTitle sx={{ borderBottom: "1px solid #e4eef8", pb: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
              <span style={{ fontSize: 20 }}>🏠</span>
              <VuiTypography variant="h6" sx={{ color: TC.primary, fontWeight: FW.bold }}>New Address Change Job</VuiTypography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm, display: "block", mb: 2.5, lineHeight: 1.7 }}>
                A new AD01 address change job will be created for <strong style={{ color: TC.primary }}>{profile?.legalName}</strong> with all 6 standard tasks ready to action.
              </VuiTypography>

              <VuiBox mb={2.5}>
                <VuiTypography variant="caption" sx={{ color: TC.primary, fontWeight: FW.semiBold, fontSize: FS.sm, display: "block", mb: 0.8 }}>
                  Due Date <span style={{ color: TC.tertiary, fontWeight: FW.regular }}>(optional)</span>
                </VuiTypography>
                <input
                  type="date"
                  value={newJobDueDate}
                  onChange={(e) => setNewJobDueDate(e.target.value)}
                  style={{
                    width: "100%", padding: "8px 12px", borderRadius: 8, border: "1.5px solid #d8e6f7",
                    fontSize: "0.875rem", color: TC.primary, background: "#f4f8ff", outline: "none",
                    fontFamily: "inherit",
                  }}
                />
              </VuiBox>

              <VuiBox mb={1}>
                <VuiTypography variant="caption" sx={{ color: TC.primary, fontWeight: FW.semiBold, fontSize: FS.sm, display: "block", mb: 0.8 }}>
                  Notes <span style={{ color: TC.tertiary, fontWeight: FW.regular }}>(optional)</span>
                </VuiTypography>
                <textarea
                  value={newJobNotes}
                  onChange={(e) => setNewJobNotes(e.target.value)}
                  placeholder="e.g. Client moving to 12 Baker Street, London, EC1A 1BB"
                  rows={3}
                  style={{
                    width: "100%", padding: "8px 12px", borderRadius: 8, border: "1.5px solid #d8e6f7",
                    fontSize: "0.875rem", color: TC.primary, background: "#f4f8ff", outline: "none",
                    fontFamily: "inherit", resize: "vertical", boxSizing: "border-box",
                  }}
                />
              </VuiBox>

              {newJobError && (
                <VuiTypography variant="caption" sx={{ color: TC.error, fontSize: FS.sm, display: "block", mt: 1 }}>
                  ⚠ {newJobError}
                </VuiTypography>
              )}
            </DialogContent>
            <DialogActions sx={{ borderTop: "1px solid #e4eef8", p: 2, gap: 1 }}>
              <VuiButton size="small" variant="outlined" onClick={() => setNewJobOpen(false)} disabled={newJobSaving}
                sx={{ color: TC.secondary, borderColor: "#d8e6f7", "&:hover": { background: "#f4f8ff" } }}>
                Cancel
              </VuiButton>
              <VuiButton size="small" variant="contained" onClick={handleCreateAd01Job} disabled={newJobSaving}
                sx={{ background: "#0075ff", color: "#fff", "&:hover": { background: "#0057d8" } }}>
                {newJobSaving ? "Creating…" : "Create Job"}
              </VuiButton>
            </DialogActions>
          </Dialog>
        </>
      )}

      {isCSTab ? (
        <ConfirmationStatementTab
          tenantId={tenantId}
          clientId={profile?.id}
          profile={profile}
          directors={directors}
          onProfileUpdated={onProfileUpdated}
          onDirectorsUpdated={onDirectorsUpdated}
        />
      ) : (
        <ServiceJobsPane jobs={filtered} loading={loading} tenantId={tenantId} profile={profile} />
      )}
    </VuiBox>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ClientProfileDetailPage() {
  const { profileId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { tenantId, hasAllPermissions, isTenantAdmin, isSuperAdmin } = useAuthz();
  const canManage = hasAllPermissions([PERMISSIONS.MANAGE_CLIENT_PROFILES]);

  // Seed from router state (instant render) then confirm via API
  const [profile, setProfile] = useState(location.state?.profile || null);
  const [loading, setLoading] = useState(!location.state?.profile);
  const [error, setError] = useState("");

  const [consents, setConsents] = useState([]);
  const [consentsLoading, setConsentsLoading] = useState(true);
  const [directors, setDirectors] = useState([]);
  const [directorsLoading, setDirectorsLoading] = useState(true);
  const [resyncing, setResyncing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [resyncMsg, setResyncMsg] = useState("");
  const [syncingDirectors, setSyncingDirectors] = useState(false);
  const [directorsMsg, setDirectorsMsg] = useState("");
  const [userMap, setUserMap] = useState({});
  const [filingPreview, setFilingPreview] = useState({ open: false, title: "", blobUrl: null, loading: false, error: "" });

  // Company profile notes — inline edit
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesDraft,   setNotesDraft]   = useState("");
  const [notesSaving,  setNotesSaving]  = useState(false);
  const [notesMsg,     setNotesMsg]     = useState("");

  // Tax identity — inline edit
  const [taxEditing, setTaxEditing] = useState(false);
  const [taxDraft,   setTaxDraft]   = useState({});
  const [taxSaving,  setTaxSaving]  = useState(false);
  const [taxMsg,     setTaxMsg]     = useState("");
  const [taxTab,     setTaxTab]     = useState(0);
  const [chTab,      setChTab]      = useState(0);
  const [profileTab, setProfileTab] = useState(location.state?.tab ?? 0);
  const [vatSummary, setVatSummary] = useState({ settings: {}, workflows: [], documents: [] });
  const [vatSummaryLoading, setVatSummaryLoading] = useState(true);
  const [vatSummaryError, setVatSummaryError] = useState("");
  const [vatSyncing, setVatSyncing] = useState(false);

  const profileVatRegistered = Boolean(getProfileVatValue(profile, "vatRegistered", "vat_registered", false));
  const profileVatNumber      = String(getProfileVatValue(profile, "vatNumber", "vat_number", "") || "");
  const profileVatScheme      = String(getProfileVatValue(profile, "vatScheme", "vat_scheme", "none") || "none");

  const refreshVatSummary = async ({ forceSync = false } = {}) => {
    if (!tenantId || !profileId) return;
    setVatSummaryLoading(true);
    setVatSummaryError("");
    try {
      const [settings, workflowsRes] = await Promise.all([
        apiRequest(`/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(profileId)}/vat-settings`),
        apiRequest(`/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(profileId)}/vat/workflows`),
      ]);

      const nextSettings = settings || {};
      const nextWorkflows = Array.isArray(workflowsRes?.workflows) ? workflowsRes.workflows : [];
      const nextDocuments = Array.isArray(workflowsRes?.documents) ? workflowsRes.documents : [];

      let syncResult = null;
      if (forceSync || nextSettings?.vrn || profileVatNumber) {
        try {
          syncResult = await apiRequest(`/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(profileId)}/vat/sync`, { method: "POST" });
        } catch (syncErr) {
          setVatSummaryError(syncErr?.message || "Unable to sync VAT obligations right now.");
        }
      }

      const mergedWorkflows = Array.isArray(syncResult?.workflows) && syncResult.workflows.length > 0
        ? syncResult.workflows
        : nextWorkflows;

      const syncMessage = syncResult?.message || (syncResult?.skipped ? "HMRC sync did not return any obligations." : "");
      if (syncMessage) {
        setVatSummaryError(syncMessage);
      }

      setVatSummary({
        settings: nextSettings,
        workflows: mergedWorkflows,
        documents: nextDocuments,
        syncResult,
      });
    } catch (err) {
      setVatSummaryError(err?.message || "Unable to load VAT details.");
      setVatSummary({ settings: {}, workflows: [], documents: [] });
    } finally {
      setVatSummaryLoading(false);
    }
  };

  // Sync tab from navigation state when navigating to the same route
  useEffect(() => {
    if (location.state?.tab !== undefined) {
      setProfileTab(location.state.tab);
    }
  }, [location]);

  const anyEditing = notesEditing || taxEditing;
  const { ConfirmDialog } = useUnsavedChanges(anyEditing);

  // Record requests
  const [rrModalOpen, setRrModalOpen]       = useState(false);
  const [recordRequests, setRecordRequests] = useState([]);
  const [rrLoading, setRrLoading]           = useState(true);

  // Email inbox
  const [inboxEmails, setInboxEmails]       = useState([]);
  const [inboxLoading, setInboxLoading]     = useState(true);
  const [inboxError, setInboxError]         = useState("");
  const [inboxEmail, setInboxEmail]         = useState(null); // currently viewed email
  const [inboxPage, setInboxPage]           = useState(0);
  const [inboxSearch, setInboxSearch]       = useState("");
  const [inboxTab, setInboxTab]             = useState(location.state?.inboxTab ?? 0); // 0 = Inbox, 1 = Sent, 2 = Deleted
  const [previewAtt, setPreviewAtt]         = useState(null); // { url, name, contentType }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deletingEmailId, setDeletingEmailId] = useState("");
  const [deleteEmailDialogOpen, setDeleteEmailDialogOpen] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [deleteEmailMode, setDeleteEmailMode] = useState("soft");
  const inboxPollInFlight = useRef(false);
  const INBOX_PAGE_SIZE = 5;

  // Compose
  const [composeOpen, setComposeOpen]       = useState(false);
  const [composeTo, setComposeTo]           = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody]       = useState("");
  const [composeSending, setComposeSending] = useState(false);
  const [composeMsg, setComposeMsg]         = useState("");
  const [inboxSyncing, setInboxSyncing]     = useState(false);
  const [inboxSyncMsg, setInboxSyncMsg]     = useState("");

  // Load profile from API (or confirm seeded data)
  useEffect(() => {
    if (!tenantId || !profileId) return;
    if (!location.state?.profile) setLoading(true);
    getClientProfile(tenantId, profileId)
      .then((data) => {
        const p = data?.clientProfile || data;
        if (p) setProfile(p);
      })
      .catch((err) => setError(err?.message || "Failed to load profile."))
      .finally(() => setLoading(false));
  }, [tenantId, profileId]);

  useEffect(() => {
    if (!tenantId || !profileId) return;
    refreshVatSummary({
      forceSync: Boolean(profileVatNumber || profile?.tax_identity?.vat_number || profile?.vatNumber),
    });
  }, [tenantId, profileId, profileVatNumber, profile?.tax_identity?.vat_number, profile?.vatNumber]);

  // Load consents, directors, userMap
  useEffect(() => {
    if (!tenantId || !profileId) return;
    listClientProfileConsents(tenantId, profileId)
      .then((res) => setConsents(res?.consents ?? []))
      .catch(() => setConsents([]))
      .finally(() => setConsentsLoading(false));

    listDirectors(tenantId, profileId)
      .then((res) => setDirectors(res?.directors ?? []))
      .catch(() => setDirectors([]))
      .finally(() => setDirectorsLoading(false));

    listTenantUsers({ tenantId })
      .then(({ users }) => {
        const map = {};
        users.forEach((u) => { if (u.uid) map[u.uid] = u.displayName || u.email || u.uid; });
        setUserMap(map);
      })
      .catch(() => {});

    listRecordRequests(tenantId, profileId)
      .then((res) => setRecordRequests(res?.recordRequests ?? []))
      .catch(() => setRecordRequests([]))
      .finally(() => setRrLoading(false));

    listClientInbox(tenantId, profileId)
      .then((res) => setInboxEmails(res?.emails ?? []))
      .catch((err) => {
        setInboxEmails([]);
        setInboxError(err?.message || "Failed to load emails.");
      })
      .finally(() => setInboxLoading(false));
  }, [tenantId, profileId]);

  // Auto-refresh inbox every 30 s while Communications tab is open
  useEffect(() => {
    if (profileTab !== 4 || !tenantId || !profileId) return;
    const id = setInterval(async () => {
      if (inboxPollInFlight.current) return;
      inboxPollInFlight.current = true;
      try {
        const res = await listClientInbox(tenantId, profileId);
        setInboxEmails(res?.emails ?? []);
        setInboxError("");
      } catch (err) {
        setInboxError(err?.message || "Failed to refresh emails.");
      } finally {
        inboxPollInFlight.current = false;
      }
    }, 30_000);
    return () => clearInterval(id);
  }, [profileTab, tenantId, profileId]);

  const handleResync = async () => {
    setResyncing(true);
    setResyncMsg("");
    try {
      const result = await resyncClientFromCH(tenantId, profileId);
      const changes = Array.isArray(result?.changes) ? result.changes : [];
      const dr = result?.directors || {};
      setResyncMsg(`Resync complete. ${changes.length} field change(s), ${dr.created || 0} new director(s), ${dr.updated || 0} updated.`);
      listDirectors(tenantId, profileId).then((res) => setDirectors(res?.directors ?? [])).catch(() => {});
      getClientProfile(tenantId, profileId).then((data) => { const p = data?.clientProfile || data; if (p) setProfile(p); }).catch(() => {});
    } catch (err) {
      setResyncMsg(err?.message || "Resync failed.");
    } finally {
      setResyncing(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!profile) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteClientProfilePermanent(tenantId, profile.id);
      history.push("/client-profiles");
    } catch (err) {
      setDeleteError(err?.message || "Failed to delete profile.");
      setDeleting(false);
    }
  };

  const handleDeleteInboxEmail = async (email) => {
    const sourceCollection = email.sourceCollection
      || (inboxTab === 2 || email.status === "deleted"
        ? "deletedEmails"
        : email.direction === "inbound"
          ? "inboundEmails"
          : "outboundEmails");
    setEmailToDelete({ ...email, sourceCollection });
    setDeleteEmailMode(sourceCollection === "deletedEmails" ? "hard" : "soft");
    setDeleteEmailDialogOpen(true);
  };

  const handleInboxSync = async () => {
    if (!tenantId || inboxSyncing) return;
    setInboxSyncing(true);
    setInboxSyncMsg("");
    try {
      const result = await triggerEmailSync(tenantId);
      const fetched = result?.fetched ?? 0;
      setInboxSyncMsg(fetched > 0 ? `✓ ${fetched} new email${fetched !== 1 ? "s" : ""} pulled` : "✓ Up to date");
      const res = await listClientInbox(tenantId, profileId);
      setInboxEmails(res?.emails ?? []);
    } catch (err) {
      setInboxSyncMsg(err?.message || "Sync failed.");
    } finally {
      setInboxSyncing(false);
      setTimeout(() => setInboxSyncMsg(""), 4000);
    }
  };

  const confirmDeleteInboxEmail = async () => {
    if (!emailToDelete) return;
    setDeletingEmailId(emailToDelete.id);
    setDeleteEmailDialogOpen(false);
    try {
      if (deleteEmailMode === "hard") {
        await hardDeleteClientInboxEmail(tenantId, profileId, emailToDelete);
      } else {
        await deleteClientInboxEmail(tenantId, profileId, emailToDelete);
      }
      const res = await listClientInbox(tenantId, profileId);
      setInboxEmails(res?.emails ?? []);
      if (inboxEmail?.id === emailToDelete.id) setInboxEmail(null);
      setInboxEmail(null);
      setEmailToDelete(null);
      setDeleteEmailMode("soft");
    } catch (err) {
      setDeleteError(err?.message || "Failed to delete email.");
    } finally {
      setDeletingEmailId("");
    }
  };

  const handleSyncDirectors = async () => {
    setSyncingDirectors(true);
    setDirectorsMsg("");
    try {
      const result = await resyncClientFromCH(tenantId, profileId);
      const dr = result?.directors || {};
      const created = dr.created || 0;
      const updated = dr.updated || 0;
      setDirectorsMsg(
        created + updated > 0
          ? `${created} new, ${updated} updated director(s).`
          : "Directors already up to date."
      );
      listDirectors(tenantId, profileId).then((res) => setDirectors(res?.directors ?? [])).catch(() => {});
    } catch (err) {
      setDirectorsMsg(err?.message || "Sync failed.");
    } finally {
      setSyncingDirectors(false);
    }
  };

  const closeFilingPreview = () => {
    setFilingPreview((prev) => {
      if (prev.blobUrl) URL.revokeObjectURL(prev.blobUrl);
      return { open: false, title: "", blobUrl: null, loading: false, error: "" };
    });
  };

  const handleViewFiling = async (f) => {
    setFilingPreview((prev) => {
      if (prev.blobUrl) URL.revokeObjectURL(prev.blobUrl);
      return {
        open: true,
        title: `${f.type || "Filing"} — ${f.date || ""}`,
        blobUrl: null,
        loading: true,
        error: "",
      };

      const handleDeleteInboxEmail = async (email) => {
        if (!window.confirm(`Delete "${email.subject || "this email"}"?`)) return;
        setDeletingEmailId(email.id);
        try {
          await deleteClientInboxEmail(tenantId, profileId, email);
          setInboxEmails((current) => current.filter((item) => item.id !== email.id));
          if (inboxEmail?.id === email.id) setInboxEmail(null);
        } catch (err) {
          console.error("[inbox] delete failed:", err);
        } finally {
          setDeletingEmailId("");
        }
      };
    });

    try {
      const url = await getFilingDocumentUrl(f.documentUrl);
      setFilingPreview((prev) => ({ ...prev, blobUrl: url, loading: false }));
    } catch (err) {
      setFilingPreview((prev) => ({ ...prev, loading: false, error: err.message || "Failed to load document" }));
    }
  };

  useEffect(() => () => {
    if (filingPreview.blobUrl) URL.revokeObjectURL(filingPreview.blobUrl);
  }, [filingPreview.blobUrl]);

  return (
    <DashboardLayout>
      {ConfirmDialog}
      <DashboardNavbar
        customTitle={profile?.legalName || "Client Profile"}
        customRoutes={["client-profiles", profile?.legalName || "Profile"]}
      />
      <VuiBox py={3}>

        {loading && (
          <LoadingSpinner label="Loading profile…" />
        )}
        {error && (
          <VuiTypography color="error" variant="button">{error}</VuiTypography>
        )}

        {profile && (
          <VuiBox display="flex" flexDirection="column" gap={3}>
            {/* ── Header Card ──────────────────────────────────────────── */}
            <Card sx={{ background: "#ffffff" }}>
              <VuiBox p={3} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <VuiBox display="flex" alignItems="center" gap={2}>
                  <VuiBox
                    sx={{
                      width: 52, height: 52, borderRadius: "12px",
                      background: "rgba(0,117,255,0.15)",
                      border: "1px solid rgba(0,117,255,0.3)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <BusinessIcon sx={{ color: TC.linkHover, fontSize: 28 }} />
                  </VuiBox>
                  <VuiBox>
                    <VuiTypography variant="h4" fontWeight="bold" sx={{ color: TC.primary }}>{profile.legalName}</VuiTypography>
                    {profile.tradingName && profile.tradingName !== profile.legalName && (
                      <VuiTypography variant="caption" sx={{ color: TC.secondary }}>Trading as: {profile.tradingName}</VuiTypography>
                    )}
                    <VuiBox display="flex" gap={1} mt={0.5} flexWrap="wrap" alignItems="center">
                      <StatusBadge status={profile.status} />
                      <VuiTypography variant="caption" sx={{ color: TC.secondary }}>{labelFor(JURISDICTIONS, profile.jurisdiction)}</VuiTypography>
                      {profile.companyRegNumber && (
                        <>
                          <VuiTypography variant="caption" sx={{ color: TC.secondary }}>·</VuiTypography>
                          <VuiTypography variant="caption" sx={{ color: TC.secondary }}>Reg: {profile.companyRegNumber}</VuiTypography>
                        </>
                      )}
                    </VuiBox>
                  </VuiBox>
                </VuiBox>
                <VuiBox display="flex" gap={1} flexWrap="wrap">
                  {profile.companyRegNumber && (
                    <VuiButton color="info" size="small" onClick={handleResync} disabled={resyncing} startIcon={<span style={{ fontSize: "1rem" }}>↻</span>} sx={{ flex: 1 }}>
                      {resyncing ? "Resyncing…" : "Resync from CH"}
                    </VuiButton>
                  )}
                  <VuiButton
                    color="info"
                    size="small"
                    onClick={() => setRrModalOpen(true)}
                    startIcon={<EmailIcon fontSize="small" />}
                    sx={{ flex: 1 }}
                  >
                    Request Records
                  </VuiButton>
                  {(isTenantAdmin || isSuperAdmin) && (
                    <VuiButton
                      color="error"
                      size="small"
                      startIcon={<DeleteOutlineRoundedIcon fontSize="small" />}
                      onClick={() => { setDeleteConfirmText(""); setDeleteError(""); setDeleteDialogOpen(true); }}
                      sx={{ flex: 1 }}
                    >
                      Delete Profile
                    </VuiButton>
                  )}
                </VuiBox>
              </VuiBox>
              {resyncMsg && (
                <VuiBox px={3} pb={2}>
                  <VuiTypography variant="caption" sx={{ color: resyncMsg.includes("failed") ? "#f44336" : "#4caf50" }}>
                    {resyncMsg}
                  </VuiTypography>
                </VuiBox>
              )}
            </Card>

            <VuiBox sx={{ mt: 2 }}>
              <AssistantPane />
            </VuiBox>

            {/* ── Delete profile confirmation dialog ── */}
            <Dialog
              open={deleteDialogOpen}
              onClose={() => !deleting && setDeleteDialogOpen(false)}
              maxWidth="xs"
              fullWidth
              PaperProps={{ sx: { background: "#0f1535", border: "1.5px solid rgba(244,67,54,0.4)", borderRadius: "16px" } }}
            >
              <DialogTitle sx={{ color: "#f44336", fontWeight: 700, pb: 0 }}>
                🗑 Delete Profile Permanently?
              </DialogTitle>
              <DialogContent sx={{ pt: 2 }}>
                <VuiTypography variant="body2" sx={{ color: "#aac8f0", mb: 2 }}>
                  This will <strong style={{ color: "#f44336" }}>permanently delete</strong> all data for{" "}
                  <strong style={{ color: "#fff" }}>{profile?.legalName || profile?.name}</strong> including
                  all subcollections and linked director profiles. This cannot be undone.
                </VuiTypography>
                <VuiTypography variant="caption" sx={{ color: "#aac8f0", display: "block", mb: 1 }}>
                  Type <strong style={{ color: "#fff" }}>{profile?.legalName || profile?.name}</strong> to confirm:
                </VuiTypography>
                <input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={profile?.legalName || profile?.name}
                  disabled={deleting}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1.5px solid rgba(244,67,54,0.4)",
                    background: "rgba(244,67,54,0.06)",
                    color: "#fff",
                    fontSize: "0.85rem",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                {deleteError && (
                  <VuiTypography variant="caption" sx={{ color: "#f44336", display: "block", mt: 1 }}>
                    ⚠ {deleteError}
                  </VuiTypography>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <VuiButton
                  size="small"
                  color="secondary"
                  onClick={() => setDeleteDialogOpen(false)}
                  disabled={deleting}
                >
                  Cancel
                </VuiButton>
                <VuiButton
                  size="small"
                  color="error"
                  disabled={deleting || deleteConfirmText !== (profile?.legalName || profile?.name)}
                  onClick={handleDeleteProfile}
                  startIcon={deleting ? null : <DeleteOutlineRoundedIcon fontSize="small" />}
                >
                  {deleting ? "Deleting…" : "Delete Permanently"}
                </VuiButton>
              </DialogActions>
            </Dialog>

            <Dialog
              open={deleteEmailDialogOpen}
              onClose={() => !deletingEmailId && setDeleteEmailDialogOpen(false)}
              maxWidth="xs"
              fullWidth
              PaperProps={{
                sx: {
                  backgroundImage: "none",
                  background: "#ffffff",
                  border: "1px solid #d8e6f7",
                  borderRadius: "16px",
                  boxShadow: "0 8px 40px rgba(0,87,216,0.12), 0 2px 8px rgba(0,0,0,0.06)",
                  color: TC.primary,
                  minWidth: 360,
                  maxWidth: 460,
                },
              }}
            >
              <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
                <VuiBox display="flex" alignItems="center" gap={1.5}>
                  <VuiBox
                    sx={{
                      background: "rgba(245,124,0,0.08)",
                      border: "1px solid rgba(245,124,0,0.25)",
                      borderRadius: "10px",
                      p: 0.8,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <WarningAmberRoundedIcon sx={{ color: "#f57c00", fontSize: "1.8rem" }} />
                  </VuiBox>
                  <VuiTypography variant="h5" fontWeight="bold" sx={{ color: TC.primary, letterSpacing: "0.01em" }}>
                    Delete Email
                  </VuiTypography>
                </VuiBox>
              </DialogTitle>
              <DialogContent sx={{ pt: 1, pb: 1, px: 3 }}>
                <VuiBox
                  sx={{
                    background: "rgba(245,124,0,0.05)",
                    border: "1px solid rgba(245,124,0,0.18)",
                    borderRadius: "12px",
                    px: 2.5,
                    py: 2,
                  }}
                >
                  <VuiTypography variant="body2" sx={{ color: TC.primary, lineHeight: 1.7, fontSize: "0.95rem" }}>
                    This will <strong style={{ color: "#f57c00" }}>move</strong>{" "}
                    <strong style={{ color: TC.primary }}>{emailToDelete?.subject || "this email"}</strong> to the Deleted folder.
                  </VuiTypography>
                  <VuiTypography variant="body2" sx={{ color: TC.secondary, mt: 1, fontSize: "0.85rem" }}>
                    Deleted emails can be permanently removed from the Deleted tab.
                  </VuiTypography>
                </VuiBox>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5, justifyContent: "flex-end" }}>
                <VuiButton
                  variant="outlined"
                  size="medium"
                  onClick={() => {
                    setDeleteEmailDialogOpen(false);
                    setEmailToDelete(null);
                    setDeleteEmailMode("soft");
                  }}
                  disabled={!!deletingEmailId}
                  sx={{
                    borderColor: "rgba(74,96,128,0.4)",
                    color: TC.secondary,
                    fontWeight: 600,
                    "&:hover": { borderColor: "#0a1628", background: "rgba(10,22,40,0.04)" },
                  }}
                >
                  Cancel
                </VuiButton>
                <VuiButton
                  size="medium"
                  onClick={confirmDeleteInboxEmail}
                  disabled={!!deletingEmailId}
                  startIcon={deletingEmailId ? null : <DeleteOutlineRoundedIcon fontSize="small" />}
                  sx={{
                    background: "linear-gradient(135deg, #fb8c00, #e65100)",
                    color: TC.white,
                    fontWeight: 700,
                    "&:hover": { background: "linear-gradient(135deg, #ffa726, #f57c00)" },
                    boxShadow: "0 4px 18px rgba(251,140,0,0.35)",
                  }}
                >
                  {deletingEmailId ? "Deleting…" : (deleteEmailMode === "hard" ? "Delete Forever" : "Delete")}
                </VuiButton>
              </DialogActions>
            </Dialog>

            {/* ── Missing contact info banner ──────────────────────── */}
            {(!profile.contactEmail || !profile.contactPhone) && (() => {
              const missing = [];
              if (!profile.contactEmail) missing.push("email address");
              if (!profile.contactPhone && !profile.contactMobile) missing.push("phone number");
              if (missing.length === 0) return null;
              return (
                <VuiBox
                  display="flex" alignItems="center" gap={1.5} px={2.5} py={1.5}
                  sx={{
                    background: "rgba(255,152,0,0.08)",
                    border: "1px solid rgba(255,152,0,0.4)",
                    borderRadius: "12px",
                  }}
                >
                  <span style={{ fontSize: "1.1rem" }}>⚠️</span>
                  <VuiTypography variant="caption" sx={{ color: "#ffb74d", lineHeight: 1.5 }}>
                    <strong style={{ color: "#ffa726" }}>Incomplete contact details</strong>
                    {" — "}missing {missing.join(" and ")}.
                    {" "}Record request emails
                    {!profile.contactEmail ? " cannot be sent" : " may be unreachable"} until this is resolved.
                    {" "}
                    <span
                      style={{ color: TC.link, cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => setProfileTab(2)}
                    >
                      Update contact details ↓
                    </span>
                  </VuiTypography>
                </VuiBox>
              );
            })()}

            {/* ── Tabbed Content Card ──────────────────────────────── */}
            <>
              <VuiBox sx={{ borderBottom: "1px solid #e4eef8", mb: 3 }}>
                {/* ── Top-level tab bar ─────────────────────────────── */}
                <Tabs
                  value={profileTab}
                  onChange={(_, v) => setProfileTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  textColor="inherit"
                  sx={{
                    "& .MuiTabs-indicator": { backgroundColor: "#0075ff", height: 3, borderRadius: "3px 3px 0 0" },
                    "& .MuiTab-root": {
                      color: TC.secondary,
                      fontSize: "0.78rem",
                      fontWeight: FW.semibold,
                      textTransform: "none",
                      minHeight: 44,
                      "&.Mui-selected": { color: TC.primary },
                    },
                    "& .MuiTabScrollButton-root": { color: TC.secondary, opacity: 1 },
                  }}
                >
                  <Tab label="Overview" />
                  <Tab label="Tax" />
                  <Tab label="Contact" />
                  <Tab label="Compliance" />
                  <Tab label="Emails" />
                  <Tab label="Services" />
                  <Tab label="Jobs & Tasks" />
                  <Tab label="Charges" />
                  <Tab label="Documents" />
                  {(isTenantAdmin || isSuperAdmin) && <Tab label="Secrets 🔒" />}
                </Tabs>
              </VuiBox>
                {/* ── Tab 0: Overview ───────────────────────────────── */}
                {profileTab === 0 && (<>
                  <Card sx={{ background: "#ffffff" }}><VuiBox p={3}>
                    <VuiTypography variant="button" fontWeight="bold"
                      sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em", display: "block", mb: 2 }}>
                      Company Profile
                    </VuiTypography>
                    <Grid container spacing={2}>
                      <DetailRow label="Legal Name"             value={profile.legalName} />
                      <DetailRow label="Trading Name"           value={profile.tradingName} />
                      <DetailRow label="Company Reg Number"     value={profile.companyRegNumber} />
                      <DetailRow label="Jurisdiction"           value={labelFor(JURISDICTIONS, profile.jurisdiction)} />
                      <DetailRow label="Incorporation Date"     value={profile.incorporationDate} />
                      <DetailRow label="Financial Year Start"   value={profile.financialYearStart} />
                      <DetailRow label="Base Currency"          value={labelFor(CURRENCIES, profile.baseCurrency)} />
                      <DetailRow label="Accounting Basis"       value={labelFor(ACCOUNTING_BASIS, profile.accountingBasis)} />
                      {/* Inline editable notes */}
                      <Grid item xs={12}>
                        <VuiBox mt={0.5} p={1.5} sx={{ background: "#f4f8ff", borderRadius: "8px", border: "1px solid #d8e6f7" }}>
                          <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={notesEditing ? 1 : 0}>
                            <VuiTypography variant="caption" fontWeight="bold"
                              sx={{ textTransform: "uppercase", fontSize: FS.xs, letterSpacing: "0.08em", color: TC.primary }}>
                              📝 Notes
                            </VuiTypography>
                            {!notesEditing ? (
                              <VuiButton size="small" color="text" variant="outlined"
                                onClick={() => { setNotesDraft(profile.companyNotes || ""); setNotesEditing(true); setNotesMsg(""); }}
                                startIcon={<EditIcon sx={{ fontSize: "13px !important" }} />}
                                sx={{ fontSize: "0.7rem" }}>
                                Edit
                              </VuiButton>
                            ) : (
                              <VuiBox display="flex" gap={1}>
                                <VuiButton size="small" color="text" variant="outlined" disabled={notesSaving}
                                  onClick={() => { setNotesEditing(false); setNotesMsg(""); }}
                                  startIcon={<CloseIcon sx={{ fontSize: "13px !important" }} />}
                                  sx={{ fontSize: "0.7rem" }}>
                                  Cancel
                                </VuiButton>
                                <VuiButton size="small" color="info" disabled={notesSaving}
                                  onClick={async () => {
                                    setNotesSaving(true);
                                    try {
                                      await updateClientProfile(tenantId, profileId, { companyNotes: notesDraft });
                                      setProfile((p) => ({ ...p, companyNotes: notesDraft }));
                                      setNotesEditing(false);
                                      setNotesMsg("Saved");
                                    } catch (err) {
                                      setNotesMsg(err?.message || "Save failed.");
                                    } finally {
                                      setNotesSaving(false);
                                    }
                                  }}
                                  startIcon={notesSaving ? <CircularProgress size={12} color="inherit" /> : <SaveIcon sx={{ fontSize: "13px !important" }} />}
                                  sx={{ fontSize: "0.7rem" }}>
                                  {notesSaving ? "Saving…" : "Save"}
                                </VuiButton>
                              </VuiBox>
                            )}
                          </VuiBox>
                          {notesMsg && (
                            <VuiTypography variant="caption" sx={{ color: notesMsg === "Saved" ? "#4caf50" : "#f44336", display: "block", mb: 1, fontSize: FS.sm }}>
                              {notesMsg === "Saved" ? "✓ Saved" : `⚠ ${notesMsg}`}
                            </VuiTypography>
                          )}
                          {notesEditing ? (
                            <textarea
                              value={notesDraft}
                              onChange={(e) => setNotesDraft(e.target.value)}
                              rows={4}
                              placeholder="Add notes about this company…"
                              style={{
                                width: "100%", background: "#f4f8ff", border: "1px solid #d8e6f7",
                                borderRadius: "6px", color: TC.primary, fontSize: FS.md, padding: "8px 10px",
                                lineHeight: 1.6, resize: "vertical", outline: "none", fontFamily: "inherit",
                              }}
                            />
                          ) : (
                            profile.companyNotes ? (
                              <VuiTypography variant="caption" sx={{ color: TC.secondary,  fontSize: "0.78rem", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                                {profile.companyNotes}
                              </VuiTypography>
                            ) : (
                              <VuiTypography variant="caption" sx={{ color: TC.tertiary,  opacity: 0.75, fontSize: FS.sm }}>
                                No notes yet — click Edit to add them.
                              </VuiTypography>
                            )
                          )}
                        </VuiBox>
                      </Grid>
                    </Grid>
                  </VuiBox></Card>

                  {/* ── Companies House Data card ───────────────────── */}
                  <Card sx={{ background: "#ffffff", mt: 2 }}><VuiBox p={3}>
                    <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
                      <VuiBox display="flex" alignItems="center" gap={1}>
                        <VuiTypography variant="button" fontWeight="bold"
                          sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em" }}>
                          Companies House Data
                        </VuiTypography>
                        {profile.companyStatus && (
                          <Chip
                            label={profile.companyStatus.toUpperCase()} size="small"
                            sx={{
                              height: 18, fontSize: "0.6rem", fontWeight: FW.bold,
                              bgcolor: profile.companyStatus === "active" ? "rgba(76,175,80,0.2)" : "rgba(244,67,54,0.2)",
                              color:   profile.companyStatus === "active" ? "#4caf50" : "#f44336",
                              border:  `1px solid ${profile.companyStatus === "active" ? "#4caf50" : "#f44336"}`,
                            }}
                          />
                        )}
                      </VuiBox>
                      {profile.chLastSyncedAt && (
                        <VuiTypography variant="caption" sx={{ color: TC.secondary,  fontSize: FS.xs, opacity: 0.8 }}>
                          Last synced {new Date(profile.chLastSyncedAt).toLocaleString("en-GB")}
                        </VuiTypography>
                      )}
                    </VuiBox>
                    {/* Inner tab bar */}
                    <Tabs
                      value={chTab}
                      onChange={(_, v) => setChTab(v)}
                      variant="scrollable"
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                      sx={{
                        mb: 2,
                        minHeight: 32,
                        "& .MuiTabs-indicator": { backgroundColor: "#0075ff" },
                        "& .MuiTab-root": { color: TC.secondary, fontSize: FS.sm, minHeight: 32, py: 0.5, textTransform: "none" },
                        "& .Mui-selected": { color: "#0a1628 !important", fontWeight: "700 !important" },
                        "& .MuiTabScrollButton-root": { color: TC.secondary, opacity: 1 },
                      }}
                    >
                      <Tab label="Overview" />
                      <Tab label={`Directors${directors.length > 0 ? ` (${directors.length})` : ""}`} />
                      <Tab label={`Filing History${profile.filingHistory?.length > 0 ? ` (${profile.filingHistory.length})` : ""}`} />
                    </Tabs>

                    {/* ── CH Overview tab ─────────────────────────────────────── */}
                    {chTab === 0 && (<>
                      {/* Company Identity */}
                      <VuiBox mb={2} p={1.5} sx={{ background: "#f4f8ff", borderRadius: "8px", border: "1px solid #d8e6f7" }}>
                        <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: FS.xs, display: "block", mb: 1 }}>
                          🏢 Company Identity
                        </VuiTypography>
                        <Grid container spacing={1.5}>
                          {[
                            { label: "CH Company Type", value: profile.companyTypeRaw },
                            { label: "Accounting Ref",  value: profile.accountingReferenceDate },
                          ].filter(f => f.value).map(f => (
                            <Grid item xs={6} sm={4} key={f.label}>
                              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm, display: "block", mb: 0.2 }}>{f.label}</VuiTypography>
                              <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.md, fontWeight: FW.medium, lineHeight: 1.4 }}>{f.value}</VuiTypography>
                            </Grid>
                          ))}
                        </Grid>
                      </VuiBox>

                      {/* Registered Address (CH) */}
                      {profile.registeredAddressObj && Object.values(profile.registeredAddressObj).some(Boolean) && (
                        <VuiBox mb={2} p={1.5} sx={{ background: "#f4f8ff", borderRadius: "8px", border: "1px solid #d8e6f7" }}>
                          <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: FS.xs, display: "block", mb: 1 }}>
                            📍 Registered Address (CH)
                          </VuiTypography>
                          <Grid container spacing={1.5}>
                            {[
                              { label: "Line 1",      value: profile.registeredAddressObj.addressLine1 },
                              { label: "Line 2",      value: profile.registeredAddressObj.addressLine2 },
                              { label: "Town / City", value: profile.registeredAddressObj.locality },
                              { label: "Region",      value: profile.registeredAddressObj.region },
                              { label: "Postcode",    value: profile.registeredAddressObj.postalCode },
                              { label: "Country",     value: profile.registeredAddressObj.country },
                            ].filter(f => f.value).map(f => (
                              <Grid item xs={6} sm={4} key={f.label}>
                                <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm, display: "block", mb: 0.2 }}>{f.label}</VuiTypography>
                                <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.md, fontWeight: FW.medium, lineHeight: 1.4 }}>{f.value}</VuiTypography>
                              </Grid>
                            ))}
                          </Grid>
                        </VuiBox>
                      )}

                      {/* Accounts & Filing Dates */}
                      <VuiBox mb={2} p={1.5} sx={{ background: "#f4f8ff", borderRadius: "8px", border: "1px solid #d8e6f7" }}>
                        <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: FS.xs, display: "block", mb: 1 }}>
                          📅 Accounts &amp; Filing Dates
                        </VuiTypography>
                        <Grid container spacing={1.5}>
                          {[
                            { label: "Next Accounts Due",     value: profile.nextAccountsDue },
                            { label: "Accounts Period End",   value: profile.nextAccountsPeriodEndOn },
                            { label: "Last Accounts Made Up", value: profile.lastAccountsMadeUpTo },
                            { label: "Last Accounts Type",    value: profile.lastAccountsType },
                            { label: "Conf. Statement Due",   value: profile.confirmationStatementNextDue },
                            { label: "Last Conf. Statement",  value: profile.confirmationStatementLastMadeUpTo },
                          ].filter(f => f.value).map(f => (
                            <Grid item xs={6} sm={4} key={f.label}>
                              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm, display: "block", mb: 0.2 }}>{f.label}</VuiTypography>
                              <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.md, fontWeight: FW.medium, lineHeight: 1.4 }}>{f.value}</VuiTypography>
                            </Grid>
                          ))}
                        </Grid>
                      </VuiBox>

                      {/* SIC Codes */}
                      {profile.sicCodes?.length > 0 && (
                        <VuiBox mb={2} p={1.5} sx={{ background: "#f4f8ff", borderRadius: "8px", border: "1px solid #d8e6f7" }}>
                          <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: FS.xs, display: "block", mb: 1 }}>
                            🏭 SIC Codes
                          </VuiTypography>
                          <VuiBox display="flex" gap={0.8} flexWrap="wrap">
                            {profile.sicCodes.map(code => (
                              <Chip key={code} label={code} size="small"
                                sx={{ bgcolor: "rgba(0,117,255,0.15)", color: TC.link, border: "1px solid rgba(0,117,255,0.3)", fontSize: FS.sm, height: 22 }} />
                            ))}
                          </VuiBox>
                        </VuiBox>
                      )}

                      {/* Risk Flags */}
                      {(profile.hasCharges || profile.hasInsolvencyHistory || profile.hasBeenLiquidated) && (
                        <VuiBox p={1.5} sx={{ background: "rgba(244,67,54,0.05)", borderRadius: "8px", border: "1px solid rgba(244,67,54,0.2)" }}>
                          <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: FS.xs, display: "block", mb: 1 }}>
                            ⚠ Risk &amp; Compliance Flags
                          </VuiTypography>
                          <VuiBox display="flex" gap={1} flexWrap="wrap">
                            {profile.hasCharges          && <Chip label="⚠ Has Charges"         size="small" sx={{ bgcolor: "rgba(255,152,0,0.15)",  color: TC.warning, border: "1px solid rgba(255,152,0,0.4)",  fontSize: "0.7rem" }} />}
                            {profile.hasInsolvencyHistory && <Chip label="🚨 Insolvency History" size="small" sx={{ bgcolor: "rgba(244,67,54,0.15)", color: TC.error, border: "1px solid rgba(244,67,54,0.4)", fontSize: "0.7rem" }} />}
                            {profile.hasBeenLiquidated   && <Chip label="🚨 Has Been Liquidated" size="small" sx={{ bgcolor: "rgba(244,67,54,0.15)", color: TC.error, border: "1px solid rgba(244,67,54,0.4)", fontSize: "0.7rem" }} />}
                          </VuiBox>
                        </VuiBox>
                      )}
                    </>)}

                    {/* ── CH Directors tab ─────────────────────────────────────── */}
                    {chTab === 1 && (
                      directorsLoading ? (
                        <LoadingSpinner size="sm" label="Loading directors…" centered={false} />
                      ) : directors.length === 0 ? (
                        <VuiBox px={2} py={2} borderRadius="8px" sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7", textAlign: "center" }}>
                          <VuiTypography variant="caption" display="block" mb={1.5} sx={{ color: TC.secondary }}>
                            No directors found.{" "}
                            {profile.companyRegNumber ? "Sync from Companies House to import directors." : "Add a company reg number first, then sync."}
                          </VuiTypography>
                          {profile.companyRegNumber && (
                            <VuiButton size="small" color="info" variant="outlined" disabled={resyncing} onClick={handleResync}
                              sx={{ fontSize: FS.sm }}>
                              {resyncing ? "Syncing…" : "↻ Pull from Companies House"}
                            </VuiButton>
                          )}
                        </VuiBox>
                      ) : (
                        <>
                          {/* Sync button bar */}
                          {profile.companyRegNumber && (
                            <VuiBox display="flex" alignItems="center" justifyContent="flex-end" mb={1.5} gap={1}>
                              {directorsMsg && (
                                <VuiTypography variant="caption" sx={{ color: TC.secondary,  fontSize: "0.68rem", opacity: 0.85 }}>
                                  {directorsMsg}
                                </VuiTypography>
                              )}
                              <VuiButton size="small" color="info" variant="outlined" disabled={syncingDirectors} onClick={handleSyncDirectors}
                                sx={{ fontSize: FS.sm }}>
                                {syncingDirectors ? "Syncing…" : "↻ Sync Directors"}
                              </VuiButton>
                            </VuiBox>
                          )}
                          <VuiBox>
                            {/* Header row */}
                            <VuiBox display="grid" sx={{ gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr", gap: 1, px: 1, pb: 0.5, borderBottom: "1px solid #e4eef8" }}>
                              {["Name", "Role", "Appointed", "DOB", "Nationality", "Status"].map((h) => (
                                <VuiTypography key={h} variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.62rem", opacity: 0.6, fontWeight: FW.bold, textTransform: "uppercase" }}>{h}</VuiTypography>
                              ))}
                            </VuiBox>
                            {/* Data rows */}
                            {directors.map((d) => (
                              <VuiBox
                                key={d.id}
                                display="grid"
                                onClick={() => {
                                  if (d.linkedClientId) {
                                    history.push(`/individual-clients/${d.linkedClientId}`);
                                  } else {
                                    history.push(`/directors?clientId=${profileId}`);
                                  }
                                }}
                                sx={{
                                  gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr",
                                  gap: 1, px: 1, py: 1,
                                  cursor: "pointer",
                                  borderBottom: "1px solid #e4eef8",
                                  borderRadius: "6px",
                                  transition: "background 0.15s",
                                  "&:hover": { background: "rgba(0,117,255,0.08)" },
                                }}
                              >
                                <VuiBox>
                                  <VuiTypography variant="caption" fontWeight="bold" display="block" sx={{ color: d.resignedOn ? TC.secondary : TC.primary, fontSize: FS.md }}>{d.fullName}</VuiTypography>
                                  {d.occupation && <VuiTypography variant="caption" sx={{ color: TC.secondary,  fontSize: FS.xs, opacity: 0.8 }} display="block">{d.occupation}</VuiTypography>}
                                  <VuiTypography variant="caption" sx={{ fontSize: "0.63rem", color: d.linkedClientId ? "#0075ff" : "rgba(255,255,255,0.3)", display: "block", mt: 0.3 }}>
                                    {d.linkedClientId ? "👤 View individual →" : "View directors →"}
                                  </VuiTypography>
                                </VuiBox>
                                <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.sm, alignSelf: "center" }}>{d.role?.replace(/_/g, " ") || "Director"}</VuiTypography>
                                <VuiBox sx={{ alignSelf: "center" }}>
                                  <VuiTypography variant="caption" display="block" sx={{ color: TC.primary, fontSize: FS.sm }}>{d.appointedOn || "—"}</VuiTypography>
                                  {d.resignedOn && <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs }}>Res: {d.resignedOn}</VuiTypography>}
                                </VuiBox>
                                <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.sm, alignSelf: "center" }}>{d.dateOfBirth || "—"}</VuiTypography>
                                <VuiBox sx={{ alignSelf: "center" }}>
                                  <VuiTypography variant="caption" display="block" sx={{ color: TC.primary, fontSize: FS.sm }}>{d.nationality || "—"}</VuiTypography>
                                  {d.countryOfResidence && d.countryOfResidence !== d.nationality && (
                                    <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.xs }}>Res: {d.countryOfResidence}</VuiTypography>
                                  )}
                                </VuiBox>
                                <VuiBox sx={{ alignSelf: "center" }}>
                                  {d.resignedOn ? (
                                    <VuiBadge variant="standard" badgeContent="Resigned" color="error" size="xs" container
                                      sx={({ palette, borders: { borderRadius, borderWidth } }) => ({ background: palette.error.main, border: `${borderWidth[1]} solid transparent`, borderRadius: borderRadius.md, color: palette.white.main })} />
                                  ) : (
                                    <VuiBadge variant="standard" badgeContent="Active" color="success" size="xs" container
                                      sx={({ palette, borders: { borderWidth, borderRadius } }) => ({ background: palette.success.main, border: `${borderWidth[1]} solid transparent`, borderRadius: borderRadius.md, color: palette.white.main })} />
                                  )}
                                </VuiBox>
                              </VuiBox>
                            ))}
                          </VuiBox>
                        </>
                      )
                    )}

                    {/* ── CH Filing History tab ───────────────────────────────── */}
                    {chTab === 2 && (
                      !profile.filingHistory?.length ? (
                        <VuiBox px={2} py={2} borderRadius="8px" sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7", textAlign: "center" }}>
                          <VuiTypography variant="caption" display="block" mb={1.5} sx={{ color: TC.secondary }}>
                            No filing history found.{" "}
                            {profile.companyRegNumber ? "Sync from Companies House to fetch the latest filings." : "Add a company reg number first, then sync."}
                          </VuiTypography>
                          {profile.companyRegNumber && (
                            <VuiButton size="small" color="info" variant="outlined" disabled={resyncing} onClick={handleResync}
                              sx={{ fontSize: FS.sm }}>
                              {resyncing ? "Syncing…" : "↻ Pull from Companies House"}
                            </VuiButton>
                          )}
                        </VuiBox>
                      ) : (
                        <VuiBox>
                          {/* Prompt resync if no documentUrls yet */}
                          {profile.filingHistory.every(f => !f.documentUrl) && (
                            <VuiBox px={2} py={1} mb={1.5} borderRadius="8px"
                              sx={{ background: "rgba(0,117,255,0.06)", border: "1px solid rgba(0,117,255,0.2)", display: "flex", alignItems: "center", gap: 1.5 }}>
                              <VuiTypography variant="caption" sx={{ color: TC.secondary, flex: 1, fontSize: FS.sm }}>
                                💡 Resync from Companies House to enable document previews for each filing.
                              </VuiTypography>
                              <VuiButton size="small" color="info" variant="outlined" disabled={resyncing} onClick={handleResync}
                                sx={{ fontSize: FS.xs, whiteSpace: "nowrap" }}>
                                {resyncing ? "Syncing…" : "↻ Resync"}
                              </VuiButton>
                            </VuiBox>
                          )}
                        <VuiBox sx={{
                          "& th": { borderBottom: ({ borders: { borderWidth }, palette: { grey } }) => `${borderWidth[1]} solid ${grey[700]}` },
                          "& .MuiTableRow-root:not(:last-child) td": { borderBottom: ({ borders: { borderWidth }, palette: { grey } }) => `${borderWidth[1]} solid ${grey[700]}` },
                        }}>
                          <Table
                            columns={[
                              { name: "date",        align: "left"   },
                              { name: "type",        align: "left"   },
                              { name: "category",    align: "left"   },
                              { name: "description", align: "left"   },
                              { name: "view",        align: "center" },
                            ]}
                            rows={(profile.filingHistory || []).map((f) => ({
                              hasBorder: true,
                              date: <VuiTypography variant="caption" sx={{ color: TC.secondary,  whiteSpace: "nowrap" }}>{f.date || "—"}</VuiTypography>,
                              type: (
                                <Chip label={f.type || "—"} size="small"
                                  sx={{ bgcolor: "rgba(0,117,255,0.15)", color: TC.link, border: "1px solid rgba(0,117,255,0.3)", fontSize: "0.68rem", height: 20 }} />
                              ),
                              category: <VuiTypography variant="caption" sx={{ color: TC.secondary,  textTransform: "capitalize" }}>{f.category?.replace(/_/g, " ") || "—"}</VuiTypography>,
                              description: (
                                <VuiBox>
                                  <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary,  fontSize: "0.8rem" }}>{f.description || "—"}</VuiTypography>
                                  {f.actionDate && f.actionDate !== f.date && (
                                    <VuiTypography variant="caption" sx={{ color: TC.secondary,  fontSize: FS.xs, opacity: 0.8 }}>Action date: {f.actionDate}</VuiTypography>
                                  )}
                                  {f.paperFiled && (
                                    <Chip label="Paper" size="small"
                                      sx={{ ml: 0.5, bgcolor: "rgba(255,152,0,0.15)", color: TC.warning, border: "1px solid rgba(255,152,0,0.4)", fontSize: FS.xs, height: 18 }} />
                                  )}
                                </VuiBox>
                              ),
                              view: f.documentUrl ? (
                                <Tooltip title="Preview document">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewFiling(f)}
                                    sx={{ color: TC.link, "&:hover": { color: TC.linkHover, background: "rgba(0,87,216,0.08)" }, borderRadius: "6px" }}
                                  >
                                    <VisibilityIcon sx={{ fontSize: "16px !important" }} />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: FS.xs }}>—</VuiTypography>
                              ),
                            }))}
                          />
                        </VuiBox>
                        </VuiBox>
                      )
                    )}

                    {/* (Confirmation Statement moved to Jobs & Tasks tab) */}
                  </VuiBox></Card>
                </>)}

                {/* ── Tab 1: Tax ─────────────────────────────────────── */}
                {profileTab === 1 && (
                  <Card sx={{ background: "#ffffff" }}><VuiBox p={3}>
                    <VuiTypography variant="button" fontWeight="bold"
                      sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em", display: "block", mb: 2 }}>
                      Tax
                    </VuiTypography>
                    {/* Tab bar */}
                    <Tabs
                      value={taxTab}
                      onChange={(_, v) => setTaxTab(v)}
                      variant="scrollable"
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                      sx={{
                        mb: 2,
                        minHeight: 32,
                        "& .MuiTabs-indicator": { backgroundColor: "#0075ff" },
                        "& .MuiTab-root": { color: TC.secondary, fontSize: FS.sm, minHeight: 32, py: 0.5, textTransform: "none" },
                        "& .Mui-selected": { color: "#0a1628 !important", fontWeight: "700 !important" },
                        "& .MuiTabScrollButton-root": { color: TC.secondary, opacity: 1 },
                      }}
                    >
                      <Tab label="VAT" />
                      <Tab label="PAYE" />
                      <Tab label="Corporation Tax" />
                    </Tabs>

                    {/* Edit / Save controls */}
                    <VuiBox display="flex" alignItems="center" justifyContent="flex-end" mb={taxEditing ? 2 : 1.5} gap={1}>
                      {!taxEditing ? (
                        <VuiButton size="small" color="text" variant="outlined"
                          onClick={() => {
                            setTaxDraft({
                              vatRegistered:        Boolean(getProfileVatValue(profile, "vatRegistered", "vat_registered", false)),
                              vatNumber:            String(getProfileVatValue(profile, "vatNumber", "vat_number", "") || ""),
                              vatScheme:            String(getProfileVatValue(profile, "vatScheme", "vat_scheme", "none") || "none"),
                              payeReference:        profile?.payeReference        || "",
                              payeOfficeNumber:     profile?.payeOfficeNumber     || "",
                              corporationTaxNumber: profile?.corporationTaxNumber || "",
                              utrNumber:            profile?.utrNumber            || "",
                            });
                            setTaxEditing(true); setTaxMsg("");
                          }}
                          startIcon={<EditIcon sx={{ fontSize: "13px !important" }} />}
                          sx={{ fontSize: "0.7rem" }}>
                          Edit
                        </VuiButton>
                      ) : (
                        <VuiBox display="flex" gap={1}>
                          <VuiButton size="small" color="text" variant="outlined" disabled={taxSaving}
                            onClick={() => { setTaxEditing(false); setTaxMsg(""); }}
                            startIcon={<CloseIcon sx={{ fontSize: "13px !important" }} />}
                            sx={{ fontSize: "0.7rem" }}>
                            Cancel
                          </VuiButton>
                          <VuiButton size="small" color="info" disabled={taxSaving}
                            onClick={async () => {
                              setTaxSaving(true);
                              try {
                                const payload = {
                                  vatRegistered: Boolean(taxDraft.vatRegistered ?? profileVatRegistered),
                                  vatNumber: String((taxDraft.vatNumber ?? profileVatNumber) || ""),
                                  vatScheme: String((taxDraft.vatScheme ?? profileVatScheme) || "none"),
                                  payeReference: taxDraft.payeReference ?? profile?.payeReference ?? "",
                                  payeOfficeNumber: taxDraft.payeOfficeNumber ?? profile?.payeOfficeNumber ?? "",
                                  corporationTaxNumber: taxDraft.corporationTaxNumber ?? profile?.corporationTaxNumber ?? "",
                                  utrNumber: taxDraft.utrNumber ?? profile?.utrNumber ?? "",
                                };

                                await Promise.all([
                                  updateClientProfile(tenantId, profileId, payload),
                                  apiRequest(`/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(profileId)}/vat-settings`, {
                                    method: "POST",
                                    body: {
                                      vatRegistered: payload.vatRegistered,
                                      vrn: payload.vatNumber?.trim() || undefined,
                                      scheme: payload.vatScheme || "standard",
                                    },
                                  }),
                                ]);

                                setProfile((p) => ({
                                  ...(p || {}),
                                  ...payload,
                                  vatRegistered: payload.vatRegistered,
                                  vatNumber: payload.vatNumber,
                                  vatScheme: payload.vatScheme,
                                  tax_identity: {
                                    ...(p?.tax_identity || {}),
                                    vat_registered: payload.vatRegistered,
                                    vat_number: payload.vatNumber,
                                    vat_scheme: payload.vatScheme,
                                  },
                                }));
                                await refreshVatSummary({ forceSync: true });
                                setTaxEditing(false); setTaxMsg("Saved");
                              } catch (err) {
                                setTaxMsg(err?.message || "Save failed.");
                              } finally { setTaxSaving(false); }
                            }}
                            startIcon={taxSaving ? <CircularProgress size={12} color="inherit" /> : <SaveIcon sx={{ fontSize: "13px !important" }} />}
                            sx={{ fontSize: "0.7rem" }}>
                            {taxSaving ? "Saving…" : "Save"}
                          </VuiButton>
                        </VuiBox>
                      )}
                    </VuiBox>

                    {taxMsg && (
                      <VuiTypography variant="caption" sx={{ color: taxMsg === "Saved" ? "#4caf50" : "#f44336", display: "block", mb: 1, fontSize: FS.sm }}>
                        {taxMsg === "Saved" ? "✓ Saved" : `⚠ ${taxMsg}`}
                      </VuiTypography>
                    )}

                    {/* ── VAT tab ─────────────────────────────────────────── */}
                    {taxTab === 0 && (
                      !taxEditing ? (
                        <VuiBox display="flex" flexDirection="column" gap={2.2}>
                          <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={1.5} flexWrap="wrap">
                            <VuiTypography variant="caption" fontWeight="bold"
                              sx={{ color: TC.primary, textTransform: "uppercase", fontSize: "0.68rem", letterSpacing: "0.08em" }}>
                              VAT automation status
                            </VuiTypography>
                            <VuiButton
                              size="small"
                              color="info"
                              variant="outlined"
                              onClick={async () => {
                                setVatSyncing(true);
                                try {
                                  await refreshVatSummary({ forceSync: true });
                                } finally {
                                  setVatSyncing(false);
                                }
                              }}
                              disabled={vatSyncing || vatSummaryLoading}
                              startIcon={vatSyncing ? <CircularProgress size={12} color="inherit" /> : <SendIcon sx={{ fontSize: "13px !important" }} />}
                              sx={{ fontSize: "0.7rem" }}
                            >
                              {vatSyncing ? "Syncing…" : "Sync now"}
                            </VuiButton>
                          </VuiBox>

                          <Grid container spacing={2}>
                            <DetailRow label="VAT Registered" value={profileVatRegistered ? "Yes" : "No"} />
                            <DetailRow label="VAT Number" value={profileVatNumber || "—"} />
                            <DetailRow label="VAT Scheme" value={labelFor(VAT_SCHEMES, profileVatScheme) || "—"} />
                            <Grid item xs={12} sm={6} md={4}>
                              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm, display: "block", mb: 0.2 }}>HMRC sync</VuiTypography>
                              <VuiTypography variant="button" fontWeight="medium" sx={{ color: TC.primary }}>
                                {vatSummaryLoading
                                  ? "Loading…"
                                  : vatSummary.workflows?.length
                                    ? `${vatSummary.workflows.length} obligation${vatSummary.workflows.length === 1 ? "" : "s"} synced`
                                    : "No HMRC obligations yet"}
                              </VuiTypography>
                            </Grid>
                          </Grid>

                          <VuiBox p={1.5} sx={{ background: "rgba(41,98,255,0.06)", border: "1px solid rgba(41,98,255,0.18)", borderRadius: "10px" }}>
                            <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary, textTransform: "uppercase", fontSize: "0.62rem", letterSpacing: "0.08em", display: "block", mb: 1 }}>
                              HMRC connection
                            </VuiTypography>
                            <HMRCOAuthConnect
                              tenantId={tenantId}
                              clientId={profileId}
                              onConnected={() => refreshVatSummary({ forceSync: true })}
                            />
                          </VuiBox>

                          {vatSummaryError && (
                            <VuiTypography variant="caption" sx={{ color: TC.error, fontSize: FS.sm }}>
                              ⚠ {vatSummaryError}
                            </VuiTypography>
                          )}

                          {!profileVatNumber && !vatSummary.settings?.vrn && (
                            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm }}>
                              Save a VAT number in this tab to let HMRC sync pull obligations and workflow stages for this client.
                            </VuiTypography>
                          )}

                          {vatSummary.workflows?.length > 0 ? (
                            <VuiBox p={1.5} sx={{ background: "rgba(0,117,255,0.06)", border: "1px solid rgba(0,117,255,0.16)", borderRadius: "10px" }}>
                              <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary, textTransform: "uppercase", fontSize: "0.62rem", letterSpacing: "0.08em", display: "block", mb: 1 }}>
                                Latest obligations
                              </VuiTypography>
                              <VuiBox display="flex" flexDirection="column" gap={1}>
                                {vatSummary.workflows.slice(0, 3).map((workflow) => (
                                  <VuiBox key={workflow.periodKey || workflow.id} p={1.1} sx={{ background: "#fff", border: "1px solid #d8e6f7", borderRadius: "8px" }}>
                                    <VuiBox display="flex" justifyContent="space-between" gap={1} flexWrap="wrap" alignItems="center">
                                      <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary, fontSize: FS.sm }}>
                                        {workflow.periodKey || `${workflow.periodStart || "—"} → ${workflow.periodEnd || "—"}`}
                                      </VuiTypography>
                                      <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm }}>
                                        Due {workflow.dueDate ? new Date(workflow.dueDate).toLocaleDateString("en-GB") : "—"}
                                      </VuiTypography>
                                    </VuiBox>
                                    <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm, display: "block", mt: 0.35 }}>
                                      Stage: {formatVatStage(workflow.stage)}
                                    </VuiTypography>
                                  </VuiBox>
                                ))}
                              </VuiBox>
                            </VuiBox>
                          ) : (
                            <VuiBox p={1.5} sx={{ background: "rgba(76,175,80,0.05)", border: "1px solid rgba(76,175,80,0.16)", borderRadius: "10px" }}>
                              <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm }}>
                                No VAT obligations have been synced yet. Save your VAT details and use Sync now to pull HMRC data into this profile.
                              </VuiTypography>
                            </VuiBox>
                          )}

                          {vatSummary.documents?.length > 0 && (
                            <VuiBox p={1.5} sx={{ background: "rgba(156,39,176,0.05)", border: "1px solid rgba(156,39,176,0.16)", borderRadius: "10px" }}>
                              <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary, textTransform: "uppercase", fontSize: "0.62rem", letterSpacing: "0.08em", display: "block", mb: 1 }}>
                                VAT documents
                              </VuiTypography>
                              <VuiBox display="flex" flexDirection="column" gap={0.8}>
                                {vatSummary.documents.slice(0, 3).map((document) => (
                                  <VuiTypography key={document.id} variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm }}>
                                    • {document.fileName || document.name || document.id}
                                  </VuiTypography>
                                ))}
                              </VuiBox>
                            </VuiBox>
                          )}
                        </VuiBox>
                      ) : (
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={6} md={4}>
                            <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary,  mb: 0.5, fontSize: FS.sm }}>VAT Registered</VuiTypography>
                            <Select size="small" value={String(taxDraft.vatRegistered)}
                              onChange={(e) => setTaxDraft((d) => ({ ...d, vatRegistered: e.target.value === "true" }))}
                              sx={{ color: TC.primary, fontSize: FS.md, width: "100%", "& .MuiOutlinedInput-notchedOutline": { bordercolor: "#7a94b0" } }}>
                              <MenuItem value="true"  sx={{ fontSize: FS.md }}>Yes</MenuItem>
                              <MenuItem value="false" sx={{ fontSize: FS.md }}>No</MenuItem>
                            </Select>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <ContactTextField label="VAT Number" value={taxDraft.vatNumber} onChange={(v) => setTaxDraft((d) => ({ ...d, vatNumber: v }))} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary,  mb: 0.5, fontSize: FS.sm }}>VAT Scheme</VuiTypography>
                            <Select size="small" value={taxDraft.vatScheme || "none"}
                              onChange={(e) => setTaxDraft((d) => ({ ...d, vatScheme: e.target.value }))}
                              sx={{ color: TC.primary, fontSize: FS.md, width: "100%", "& .MuiOutlinedInput-notchedOutline": { bordercolor: "#7a94b0" } }}>
                              {VAT_SCHEMES.map((v) => <MenuItem key={v.value} value={v.value} sx={{ fontSize: FS.md }}>{v.label}</MenuItem>)}
                            </Select>
                          </Grid>
                        </Grid>
                      )
                    )}

                    {/* ── PAYE tab ─────────────────────────────────────────── */}
                    {taxTab === 1 && (
                      !taxEditing ? (
                        <Grid container spacing={2}>
                          <DetailRow label="PAYE Reference"    value={profile.payeReference    || "—"} />
                          <DetailRow label="PAYE Office Number" value={profile.payeOfficeNumber || "—"} />
                        </Grid>
                      ) : (
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={6}>
                            <ContactTextField label="PAYE Reference" value={taxDraft.payeReference} onChange={(v) => setTaxDraft((d) => ({ ...d, payeReference: v }))} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <ContactTextField label="PAYE Office Number" value={taxDraft.payeOfficeNumber} onChange={(v) => setTaxDraft((d) => ({ ...d, payeOfficeNumber: v }))} />
                          </Grid>
                        </Grid>
                      )
                    )}

                    {/* ── Corporation Tax tab ──────────────────────────────── */}
                    {taxTab === 2 && (
                      !taxEditing ? (
                        <Grid container spacing={2}>
                          <DetailRow label="Corporation Tax No" value={profile.corporationTaxNumber || "—"} />
                          <DetailRow label="UTR Number"         value={profile.utrNumber            || "—"} />
                        </Grid>
                      ) : (
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={6}>
                            <ContactTextField label="Corporation Tax No" value={taxDraft.corporationTaxNumber} onChange={(v) => setTaxDraft((d) => ({ ...d, corporationTaxNumber: v }))} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <ContactTextField label="UTR Number" value={taxDraft.utrNumber} onChange={(v) => setTaxDraft((d) => ({ ...d, utrNumber: v }))} />
                          </Grid>
                        </Grid>
                      )
                    )}
                  </VuiBox></Card>
                )}

                {/* ── Tab 2: Contact ────────────────────────────────── */}
                {profileTab === 2 && (
                  <Card sx={{ background: "#ffffff" }}><VuiBox p={3}>
                    <VuiTypography variant="button" fontWeight="bold"
                      sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em", display: "block", mb: 2 }}>
                      Client Contact
                    </VuiTypography>
                    <EditableContactSection
                      profile={profile}
                      tenantId={tenantId}
                      clientId={profileId}
                      onUpdated={() => {
                        getClientProfile(tenantId, profileId)
                          .then((data) => { const p = data?.clientProfile || data; if (p) setProfile(p); })
                          .catch(() => {});
                      }}
                    />
                    <Divider sx={{ bordercolor: "#7a94b0", my: 3 }} />
                    <VuiTypography variant="button" fontWeight="bold"
                      sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em", display: "block", mb: 2 }}>
                      Accountant Relationship
                    </VuiTypography>
                    <Grid container spacing={2}>
                      <DetailRow label="Access Level"        value={labelFor(ACCESS_LEVELS, profile.accessLevel)} />
                      <DetailRow label="Onboarding Status"   value={labelFor(ONBOARDING_STATUS, profile.onboardingStatus)} />
                      <DetailRow label="Assigned Accountant" value={userMap[profile.assignedAccountantUid] || profile.assignedAccountantUid} />
                    </Grid>
                  </VuiBox></Card>
                )}

                {/* ── Tab 3: Compliance ─────────────────────────────── */}
                {profileTab === 3 && (
                  <Card sx={{ background: "#ffffff" }}><VuiBox p={3}>
                    <VuiTypography variant="button" fontWeight="bold"
                      sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em", display: "block", mb: 2 }}>
                      Related Parties
                    </VuiTypography>
                    <RelatedPartiesTab
                      tenantId={tenantId}
                      clientId={profileId}
                      hasChNumber={!!profile.companyRegNumber}
                    />
                    <Divider sx={{ bordercolor: "#7a94b0", my: 3 }} />
                    <VuiTypography variant="button" fontWeight="bold"
                      sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em", display: "block", mb: 2 }}>
                      Bank Consents
                    </VuiTypography>
                    <VuiTypography variant="caption" display="block" mb={1.5} sx={{ color: TC.secondary }}>
                      All bank consents owned by this client profile.
                    </VuiTypography>
                    {consentsLoading ? (
                      <LoadingSpinner size="sm" label="Loading consents…" centered={false} />
                    ) : consents.length === 0 ? (
                      <VuiBox px={2} py={1.5} borderRadius="8px" sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7" }}>
                        <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                          No bank consents yet. Request one from the Bank Feeds page.
                        </VuiTypography>
                      </VuiBox>
                    ) : (
                      <VuiBox sx={{
                        "& th": { borderBottom: ({ borders: { borderWidth }, palette: { grey } }) => `${borderWidth[1]} solid ${grey[700]}` },
                        "& .MuiTableRow-root:not(:last-child) td": { borderBottom: ({ borders: { borderWidth }, palette: { grey } }) => `${borderWidth[1]} solid ${grey[700]}` },
                      }}>
                        <Table
                          columns={[
                            { name: "bank",    align: "left"   },
                            { name: "status",  align: "center" },
                            { name: "granted", align: "left"   },
                            { name: "expires", align: "left"   },
                          ]}
                          rows={consents.map((c) => ({
                            hasBorder: true,
                            bank:    <VuiTypography variant="caption" fontWeight="medium" sx={{ color: TC.primary }}>{c.bank || "—"}</VuiTypography>,
                            status: (
                              <VuiBadge variant="standard" badgeContent={c.status} size="xs" container
                                color={c.status === "active" ? "success" : c.status === "pending" ? "info" : "error"}
                                sx={({ palette, borders: { borderRadius, borderWidth } }) => ({
                                  background: c.status === "active" ? palette.success.main : c.status === "pending" ? palette.info.main : palette.error.main,
                                  border: `${borderWidth[1]} solid transparent`, borderRadius: borderRadius.md, color: palette.white.main,
                                })}
                              />
                            ),
                            granted: <VuiTypography variant="caption" sx={{ color: TC.secondary }}>{c.createdAt ? new Date(c.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}</VuiTypography>,
                            expires: <VuiTypography variant="caption" sx={{ color: TC.secondary }}>{c.expiresAt ? new Date(c.expiresAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}</VuiTypography>,
                          }))}
                        />
                      </VuiBox>
                    )}
                  </VuiBox></Card>
                )}

                {/* ── Tab 4: Communications ─────────────────────────── */}
                {profileTab === 4 && (
                  <Card sx={{ background: "#ffffff" }}><VuiBox p={3}>
                    {(() => {
                      const q = inboxSearch.trim().toLowerCase();
                      const folderCounts = {
                        inbox: inboxEmails.filter((e) => e.direction === "inbound").length,
                        sent: inboxEmails.filter((e) => e.direction === "outbound").length,
                        deleted: inboxEmails.filter((e) => e.sourceCollection === "deletedEmails").length,
                      };
                      const folderItems = [
                        { key: 0, label: "Inbox", icon: <InboxIcon sx={{ fontSize: 18 }} />, count: folderCounts.inbox },
                        { key: 1, label: "Sent", icon: <SendIcon sx={{ fontSize: 17 }} />, count: folderCounts.sent },
                        { key: 2, label: "Deleted", icon: <DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />, count: folderCounts.deleted },
                      ];
                      const dirFiltered = inboxEmails.filter((e) => {
                        if (inboxTab === 0) return e.direction === "inbound" && e.sourceCollection !== "deletedEmails";
                        if (inboxTab === 1) return e.direction === "outbound" && e.sourceCollection !== "deletedEmails";
                        return e.sourceCollection === "deletedEmails";
                      });
                      const filtered = q
                        ? dirFiltered.filter((e) =>
                            (e.subject || "").toLowerCase().includes(q) ||
                            (e.from || "").toLowerCase().includes(q) ||
                            (e.to || "").toLowerCase().includes(q) ||
                            (e.status || "").toLowerCase().includes(q) ||
                            (e.bodyPreview || "").toLowerCase().includes(q) ||
                            (e.attachments || []).some((a) => (a.name || "").toLowerCase().includes(q))
                          )
                        : dirFiltered;
                      const inboxTotalPages = Math.max(1, Math.ceil(filtered.length / INBOX_PAGE_SIZE));
                      const safePg = Math.min(inboxPage, inboxTotalPages - 1);
                      const pageEmails = filtered.slice(safePg * INBOX_PAGE_SIZE, safePg * INBOX_PAGE_SIZE + INBOX_PAGE_SIZE);
                      const activeEmail = filtered.find((e) => e.id === inboxEmail?.id) || null;
                      const formatDate = (date) => date
                        ? new Date(date).toLocaleString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "";
                      const getStatusColor = (status) => ({
                        sent: { c: "#90caf9", bg: "rgba(0,117,255,0.1)" },
                        responded: { c: "#81c784", bg: "rgba(76,175,80,0.1)" },
                        reminded: { c: "#ffb74d", bg: "rgba(255,152,0,0.1)" },
                        received: { c: "#ce93d8", bg: "rgba(156,39,176,0.1)" },
                      }[status] || { c: "#9e9e9e", bg: "rgba(255,255,255,0.05)" });
                      const mjStatusConfig = {
                        queued: { label: "⏳ Queued", c: "#90caf9", bg: "rgba(0,117,255,0.1)" },
                        sent: { label: "✉️ Delivered", c: "#81c784", bg: "rgba(76,175,80,0.1)" },
                        opened: { label: "👁 Opened", c: "#4dd0e1", bg: "rgba(0,188,212,0.1)" },
                        clicked: { label: "🖱 Clicked", c: "#aed581", bg: "rgba(139,195,74,0.1)" },
                        bounce: { label: "↩ Bounced", c: "#ef9a9a", bg: "rgba(244,67,54,0.1)" },
                        blocked: { label: "🚫 Blocked", c: "#ef9a9a", bg: "rgba(244,67,54,0.1)" },
                        spam: { label: "⚠️ Spam", c: "#ffcc80", bg: "rgba(255,152,0,0.12)" },
                        unsub: { label: "🔕 Unsubscribed", c: "#bcaaa4", bg: "rgba(121,85,72,0.15)" },
                      };
                      const renderMetaChips = (email) => {
                        const statusColor = getStatusColor(email.status);
                        const mjChip = email.mailjetStatus
                          ? mjStatusConfig[email.mailjetStatus] || { label: email.mailjetStatus, c: "#9e9e9e", bg: "rgba(255,255,255,0.05)" }
                          : null;
                        return (
                          <>
                            <Chip
                              label={email.status}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.58rem",
                                fontWeight: FW.bold,
                                bgcolor: statusColor.bg,
                                color: statusColor.c,
                                border: `1px solid ${statusColor.c}44`,
                                textTransform: "capitalize",
                              }}
                            />
                            {mjChip && (
                              <Chip
                                label={mjChip.label}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "0.58rem",
                                  fontWeight: FW.bold,
                                  bgcolor: mjChip.bg,
                                  color: mjChip.c,
                                  border: `1px solid ${mjChip.c}44`,
                                }}
                              />
                            )}
                            {email.tag && (
                              <Chip
                                label={email.tag}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "0.58rem",
                                  fontWeight: FW.bold,
                                  bgcolor: email.type === "cs01_review" ? "rgba(33,150,243,0.12)" : "rgba(76,175,80,0.12)",
                                  color: email.type === "cs01_review" ? "#1e88e5" : "#43a047",
                                  border: `1px solid ${email.type === "cs01_review" ? "#1e88e544" : "#43a04744"}`,
                                }}
                              />
                            )}
                          </>
                        );
                      };

                      return (
                        <>
                          {inboxError && (
                            <VuiBox mb={1.5} px={2} py={1.25} borderRadius="8px" sx={{ background: "rgba(244,67,54,0.08)", border: "1px solid rgba(244,67,54,0.22)" }}>
                              <VuiTypography variant="caption" sx={{ color: "#d32f2f", fontSize: "0.72rem" }}>
                                {inboxError}
                              </VuiTypography>
                            </VuiBox>
                          )}

                          <Collapse in={composeOpen}>
                            <VuiBox mb={2} sx={{ border: "1px solid #d8e6f7", background: "linear-gradient(180deg, rgba(0,117,255,0.05) 0%, rgba(244,248,255,0.95) 100%)", borderRadius: "12px", px: 2, pt: 2, pb: 2.25 }}>
                              <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary, textTransform: "uppercase", fontSize: FS.xs, display: "block", mb: 1.5 }}>
                                ✉️ New Email
                              </VuiTypography>
                              <VuiBox display="flex" flexDirection="column" gap={1.5}>
                                <TextField
                                  label="To"
                                  value={composeTo}
                                  onChange={(e) => setComposeTo(e.target.value)}
                                  size="small"
                                  fullWidth
                                  variant="outlined"
                                  sx={INPUT_SX}
                                  InputLabelProps={LABEL_NO_SELECT}
                                  placeholder={profile.contactEmail || "client@example.com"}
                                />
                                <TextField
                                  label="Subject"
                                  value={composeSubject}
                                  onChange={(e) => setComposeSubject(e.target.value)}
                                  size="small"
                                  fullWidth
                                  variant="outlined"
                                  sx={INPUT_SX}
                                  InputLabelProps={LABEL_NO_SELECT}
                                />
                                <NativeTextarea
                                  label="Message"
                                  value={composeBody}
                                  onChange={setComposeBody}
                                  rows={6}
                                  placeholder="Write your message here…"
                                />
                                <VuiBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                                  {composeMsg && (
                                    <VuiTypography variant="caption" sx={{ fontSize: FS.sm, color: composeMsg.startsWith("✓") ? "#4caf50" : "#f44336" }}>
                                      {composeMsg}
                                    </VuiTypography>
                                  )}
                                  <VuiBox display="flex" gap={1} ml="auto">
                                    <VuiButton
                                      size="small"
                                      color="text"
                                      variant="outlined"
                                      onClick={() => { setComposeOpen(false); setComposeMsg(""); }}
                                      sx={{ fontSize: "0.7rem" }}
                                    >
                                      Cancel
                                    </VuiButton>
                                    <VuiButton
                                      size="small"
                                      color="info"
                                      disabled={composeSending || !composeSubject.trim() || !composeBody.trim()}
                                      onClick={async () => {
                                        setComposeSending(true);
                                        setComposeMsg("");
                                        try {
                                          await sendClientEmail(tenantId, profileId, {
                                            subject: composeSubject.trim(),
                                            bodyText: composeBody.trim(),
                                            toEmail: composeTo.trim() || undefined,
                                          });
                                          setComposeMsg("✓ Email sent successfully");
                                          setComposeSubject("");
                                          setComposeBody("");
                                          setComposeOpen(false);
                                          listClientInbox(tenantId, profileId)
                                            .then((res) => {
                                              setInboxEmails(res?.emails ?? []);
                                              setInboxError("");
                                            })
                                            .catch((err) => setInboxError(err?.message || "Failed to refresh emails."));
                                        } catch (err) {
                                          setComposeMsg(err?.message || "Failed to send email.");
                                        } finally {
                                          setComposeSending(false);
                                        }
                                      }}
                                      startIcon={composeSending ? <CircularProgress size={12} color="inherit" /> : <SendIcon sx={{ fontSize: "13px !important" }} />}
                                      sx={{ fontSize: "0.7rem" }}
                                    >
                                      {composeSending ? "Sending…" : "Send Email"}
                                    </VuiButton>
                                  </VuiBox>
                                </VuiBox>
                              </VuiBox>
                            </VuiBox>
                          </Collapse>

                          <VuiBox
                            sx={{
                              height: 620,
                              display: "flex",
                              border: "1px solid #d8e6f7",
                              borderRadius: "16px",
                              overflow: "hidden",
                              background: "#fbfdff",
                              boxShadow: "0 12px 30px rgba(15, 55, 110, 0.08)",
                            }}
                          >
                            <VuiBox
                              sx={{
                                width: 196,
                                minWidth: 196,
                                borderRight: "1px solid #e4eef8",
                                p: 1.5,
                                background: "linear-gradient(180deg, #f8fbff 0%, #f1f6fd 100%)",
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.25,
                                overflowY: "auto",
                              }}
                            >
                              <VuiButton
                                size="small"
                                color="info"
                                variant="contained"
                                onClick={() => {
                                  setComposeTo(profile.contactEmail || "");
                                  setComposeSubject("");
                                  setComposeBody("");
                                  setComposeMsg("");
                                  setComposeOpen((o) => !o);
                                }}
                                startIcon={<SendIcon sx={{ fontSize: "13px !important" }} />}
                                sx={{ fontSize: "0.72rem", py: 0.75, borderRadius: "12px" }}
                              >
                                {composeOpen ? "Close" : "Compose"}
                              </VuiButton>
                              <VuiButton
                                color="info"
                                size="small"
                                variant="outlined"
                                onClick={() => setRrModalOpen(true)}
                                startIcon={<EmailIcon sx={{ fontSize: "13px !important" }} />}
                                sx={{ fontSize: "0.7rem", py: 0.55, borderRadius: "12px" }}
                              >
                                New Request
                              </VuiButton>
                              <VuiButton
                                color="info"
                                size="small"
                                variant="outlined"
                                onClick={handleInboxSync}
                                disabled={inboxSyncing}
                                startIcon={inboxSyncing ? <CircularProgress size={10} color="inherit" /> : <span style={{ fontSize: "1rem", lineHeight: 1 }}>↻</span>}
                                sx={{ fontSize: "0.7rem", py: 0.55, borderRadius: "12px" }}
                              >
                                {inboxSyncing ? "Syncing…" : "Sync Now"}
                              </VuiButton>
                              {inboxSyncMsg && (
                                <VuiTypography variant="caption" sx={{ fontSize: "0.63rem", textAlign: "center", color: inboxSyncMsg.startsWith("✓") ? "#4caf50" : "#f44336" }}>
                                  {inboxSyncMsg}
                                </VuiTypography>
                              )}
                              <Divider sx={{ borderColor: "#d8e6f7", my: 0.25 }} />
                              <VuiBox display="flex" flexDirection="column" gap={0.6}>
                                {folderItems.map((item) => {
                                  const active = inboxTab === item.key;
                                  return (
                                    <VuiBox
                                      key={item.key}
                                      onClick={() => { setInboxTab(item.key); setInboxPage(0); setInboxEmail(null); }}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        px: 1.2,
                                        py: 0.95,
                                        borderRadius: "999px",
                                        cursor: "pointer",
                                        background: active ? "linear-gradient(90deg, rgba(0,117,255,0.14) 0%, rgba(0,117,255,0.06) 100%)" : "transparent",
                                        border: `1px solid ${active ? "rgba(0,117,255,0.26)" : "transparent"}`,
                                        transition: "all 0.15s ease",
                                        "&:hover": { background: active ? "linear-gradient(90deg, rgba(0,117,255,0.16) 0%, rgba(0,117,255,0.08) 100%)" : "rgba(0,117,255,0.05)" },
                                      }}
                                    >
                                      <VuiBox display="flex" alignItems="center" gap={1} minWidth={0}>
                                        <VuiBox sx={{ color: active ? "#0075ff" : TC.secondary, display: "flex", alignItems: "center" }}>
                                          {item.icon}
                                        </VuiBox>
                                        <VuiTypography variant="caption" sx={{ color: active ? TC.primary : TC.secondary, fontSize: "0.76rem", fontWeight: active ? FW.bold : FW.regular }}>
                                          {item.label}
                                        </VuiTypography>
                                      </VuiBox>
                                      <VuiBadge
                                        badgeContent={item.count}
                                        size="xs"
                                        container
                                        sx={{
                                          "& .MuiBadge-badge": {
                                            fontWeight: 900,
                                            fontSize: "0.72rem",
                                            background: active ? "rgba(0,117,255,0.08)" : "#fff",
                                            border: "1px solid rgba(0,117,255,0.18)",
                                            color: "#0075ff",
                                            borderRadius: "999px",
                                            boxShadow: "none",
                                            padding: "2px 6px",
                                          },
                                        }}
                                      />
                                    </VuiBox>
                                  );
                                })}
                              </VuiBox>
                            </VuiBox>

                            <VuiBox sx={{ width: 332, minWidth: 332, borderRight: "1px solid #e4eef8", display: "flex", flexDirection: "column", background: "#ffffff" }}>
                              <VuiBox p={1.25} sx={{ borderBottom: "1px solid #e4eef8", background: "#f8fbff" }}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  placeholder="Search subject, from, status, attachment…"
                                  value={inboxSearch}
                                  onChange={(e) => { setInboxSearch(e.target.value); setInboxPage(0); setInboxEmail(null); }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <SearchIcon sx={{ color: TC.secondary, fontSize: FS.base }} />
                                      </InputAdornment>
                                    ),
                                    endAdornment: inboxSearch ? (
                                      <InputAdornment position="end">
                                        <CloseIcon
                                          sx={{ color: TC.secondary, fontSize: FS.md, cursor: "pointer", "&:hover": { color: "#0075ff" } }}
                                          onClick={() => { setInboxSearch(""); setInboxPage(0); setInboxEmail(null); }}
                                        />
                                      </InputAdornment>
                                    ) : null,
                                  }}
                                  sx={{
                                    "& .MuiInputBase-root": { color: TC.primary, background: "#fff", borderRadius: "10px", fontSize: "0.78rem" },
                                    "& .MuiInputBase-input": { py: "7px", px: "8px" },
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d0def1" },
                                    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#7a94b0" },
                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0075ff" },
                                  }}
                                />
                              </VuiBox>

                              <VuiBox sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 1 }}>
                                {inboxLoading ? (
                                  <VuiBox sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <LoadingSpinner size="sm" label="Loading emails…" centered={false} />
                                  </VuiBox>
                                ) : inboxEmails.length === 0 ? (
                                  <VuiBox sx={{ height: "100%", px: 2, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.2 }}>
                                    <EmailIcon sx={{ color: "#90a4b8", fontSize: 34 }} />
                                    <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.75rem", lineHeight: 1.6 }}>
                                      No emails yet. Outbound record requests and inbound client replies will appear here.
                                    </VuiTypography>
                                  </VuiBox>
                                ) : pageEmails.length === 0 ? (
                                  <VuiBox sx={{ height: "100%", px: 2, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.2 }}>
                                    <SearchIcon sx={{ color: "#90a4b8", fontSize: 30 }} />
                                    <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.75rem", lineHeight: 1.6 }}>
                                      {q
                                        ? <>No emails match <strong style={{ color: "#0075ff" }}>&quot;{inboxSearch}&quot;</strong></>
                                        : inboxTab === 0
                                          ? "No received emails yet. Client replies will appear here."
                                          : inboxTab === 1
                                            ? "No sent emails yet. Record requests and custom emails will appear here."
                                            : "No deleted emails yet."}
                                    </VuiTypography>
                                  </VuiBox>
                                ) : (
                                  <VuiBox display="flex" flexDirection="column" gap={0.9}>
                                    {pageEmails.map((email) => {
                                      const isIn = email.direction === "inbound";
                                      const dateStr = formatDate(email.date);
                                      const counterparty = isIn ? email.from : email.to;
                                      const statusColor = getStatusColor(email.status);
                                      const mjChip = email.mailjetStatus
                                        ? mjStatusConfig[email.mailjetStatus] || { label: email.mailjetStatus, c: "#9e9e9e", bg: "rgba(255,255,255,0.05)" }
                                        : null;

                                      return (
                                        <VuiBox
                                          key={email.id}
                                          onClick={() => setInboxEmail(email)}
                                          sx={{
                                            p: 1.25,
                                            borderRadius: "12px",
                                            border: `1px solid ${activeEmail?.id === email.id ? "rgba(0,117,255,0.34)" : "#e1ebf7"}`,
                                            background: activeEmail?.id === email.id ? "rgba(0,117,255,0.08)" : "#ffffff",
                                            cursor: "pointer",
                                            transition: "all 0.15s ease",
                                            boxShadow: activeEmail?.id === email.id ? "0 8px 18px rgba(0,117,255,0.08)" : "none",
                                            "&:hover": { background: "#f8fbff", borderColor: "#9cbce0" },
                                          }}
                                        >
                                          <VuiBox display="flex" alignItems="flex-start" gap={1}>
                                            <VuiBox
                                              sx={{
                                                width: 34,
                                                height: 34,
                                                borderRadius: "10px",
                                                flexShrink: 0,
                                                background: isIn ? "rgba(156,39,176,0.12)" : "rgba(0,117,255,0.1)",
                                                border: `1px solid ${isIn ? "rgba(156,39,176,0.2)" : "rgba(0,117,255,0.18)"}`,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: FS.md,
                                              }}
                                            >
                                              {isIn ? "📥" : "📤"}
                                            </VuiBox>
                                            <VuiBox flex={1} minWidth={0}>
                                              <VuiBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} mb={0.35}>
                                                <VuiBox minWidth={0}>
                                                  <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.68rem", display: "block", mb: 0.15 }}>
                                                    {isIn ? "From" : "To"}: {counterparty || "—"}
                                                  </VuiTypography>
                                                  <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.78rem", fontWeight: FW.bold, lineHeight: 1.35, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {email.subject || "(No subject)"}
                                                  </VuiTypography>
                                                </VuiBox>
                                                <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: "0.64rem", whiteSpace: "nowrap", flexShrink: 0 }}>
                                                  {dateStr}
                                                </VuiTypography>
                                              </VuiBox>

                                              {email.bodyPreview && (
                                                <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.68rem", lineHeight: 1.45, display: "block", mb: 0.8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                  {email.bodyPreview}
                                                </VuiTypography>
                                              )}

                                              <VuiBox display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                                                <Chip
                                                  label={email.status}
                                                  size="small"
                                                  sx={{ height: 17, fontSize: "0.56rem", fontWeight: FW.bold, bgcolor: statusColor.bg, color: statusColor.c, border: `1px solid ${statusColor.c}44`, textTransform: "capitalize" }}
                                                />
                                                {mjChip && (
                                                  <Chip
                                                    label={mjChip.label}
                                                    size="small"
                                                    sx={{ height: 17, fontSize: "0.56rem", fontWeight: FW.bold, bgcolor: mjChip.bg, color: mjChip.c, border: `1px solid ${mjChip.c}44` }}
                                                  />
                                                )}
                                                {email.tag && (
                                                  <Chip
                                                    label={email.tag}
                                                    size="small"
                                                    sx={{
                                                      height: 17,
                                                      fontSize: "0.56rem",
                                                      fontWeight: FW.bold,
                                                      bgcolor: email.type === "cs01_review" ? "rgba(33,150,243,0.12)" : "rgba(76,175,80,0.12)",
                                                      color: email.type === "cs01_review" ? "#1e88e5" : "#43a047",
                                                      border: `1px solid ${email.type === "cs01_review" ? "#1e88e544" : "#43a04744"}`,
                                                    }}
                                                  />
                                                )}
                                                {email.attachments?.length > 0 && (
                                                  <Tooltip title="Preview attachments" placement="top">
                                                    <Chip
                                                      label={`${email.attachments.length} attachment${email.attachments.length !== 1 ? "s" : ""}`}
                                                      size="small"
                                                      clickable
                                                      onClick={async (e) => {
                                                        e.stopPropagation();
                                                        setInboxEmail(email);
                                                        const firstAttachment = email.attachments?.[0];
                                                        if (!firstAttachment) return;
                                                        if (!firstAttachment.storagePath) {
                                                          console.warn("[attachment] attachment has no storagePath, cannot preview/download:", firstAttachment);
                                                          setPreviewAtt({ url: null, name: firstAttachment.name || "(unknown)", contentType: firstAttachment.contentType || null, error: "Attachment not uploaded to storage." });
                                                          return;
                                                        }
                                                        setPreviewLoading(true);
                                                        try {
                                                          const res = await getAttachmentUrl(tenantId, profileId, email.id, 0, "preview");
                                                          setPreviewAtt({ url: res.url, name: res.name, contentType: res.contentType });
                                                        } catch (err) {
                                                          console.error("[attachment] Failed to get URL:", err?.message);
                                                          setPreviewAtt({ url: null, name: firstAttachment.name || "(unknown)", contentType: firstAttachment.contentType || null, error: err?.message || "Failed to get URL" });
                                                        } finally {
                                                          setPreviewLoading(false);
                                                        }
                                                      }}
                                                      sx={{
                                                        height: 17,
                                                        fontSize: "0.56rem",
                                                        bgcolor: "transparent",
                                                        color: "#0075ff",
                                                        border: "1px solid rgba(0,117,255,0.18)",
                                                        borderRadius: "999px",
                                                        fontWeight: 700,
                                                        "&:hover": { background: "rgba(0,117,255,0.06)" },
                                                      }}
                                                    />
                                                  </Tooltip>
                                                )}
                                                {email.reminderCount > 0 && (
                                                  <Chip
                                                    label={`${email.reminderCount} reminder${email.reminderCount !== 1 ? "s" : ""}`}
                                                    size="small"
                                                    sx={{ height: 17, fontSize: "0.56rem", fontWeight: FW.bold, bgcolor: "rgba(255,152,0,0.1)", color: "#ff9800", border: "1px solid rgba(255,152,0,0.2)" }}
                                                  />
                                                )}
                                                <Tooltip title={inboxTab === 2 || email.sourceCollection === "deletedEmails" ? "Delete forever" : "Move to Deleted"} placement="top">
                                                  <IconButton
                                                    size="small"
                                                    disabled={deletingEmailId === email.id}
                                                    onClick={(e) => { e.stopPropagation(); handleDeleteInboxEmail(email); }}
                                                    sx={{
                                                      ml: "auto",
                                                      width: 24,
                                                      height: 24,
                                                      borderRadius: "8px",
                                                      color: "#ff5252",
                                                      bgcolor: "rgba(244,67,54,0.12)",
                                                      border: "1px solid rgba(244,67,54,0.18)",
                                                      "&:hover": { bgcolor: "rgba(244,67,54,0.2)" },
                                                    }}
                                                  >
                                                    {inboxTab === 2 || email.sourceCollection === "deletedEmails"
                                                      ? <DeleteForeverOutlinedIcon sx={{ fontSize: 14, color: "#ff5252" }} />
                                                      : <DeleteOutlineRoundedIcon sx={{ fontSize: 14, color: "#ff5252" }} />}
                                                  </IconButton>
                                                </Tooltip>
                                              </VuiBox>
                                            </VuiBox>
                                          </VuiBox>
                                        </VuiBox>
                                      );
                                    })}
                                  </VuiBox>
                                )}
                              </VuiBox>

                              {inboxTotalPages > 1 && (
                                <VuiBox display="flex" alignItems="center" justifyContent="space-between" px={1.25} py={1} sx={{ borderTop: "1px solid #e4eef8", background: "#f8fbff" }}>
                                  <VuiTypography sx={{ color: TC.secondary, fontSize: "0.66rem" }}>
                                    {safePg * INBOX_PAGE_SIZE + 1}–{Math.min(safePg * INBOX_PAGE_SIZE + INBOX_PAGE_SIZE, filtered.length)} of {filtered.length}{q ? " filtered" : ""}
                                  </VuiTypography>
                                  <VuiBox display="flex" alignItems="center" gap={0.45}>
                                    <VuiBox
                                      onClick={() => { if (safePg > 0) { setInboxPage(safePg - 1); setInboxEmail(null); } }}
                                      px={0.85}
                                      py={0.3}
                                      sx={{ cursor: safePg === 0 ? "default" : "pointer", borderRadius: "7px", border: "1px solid #d8e6f7", opacity: safePg === 0 ? 0.35 : 1, "&:hover": safePg > 0 ? { background: "#f0f6ff" } : {} }}
                                    >
                                      <VuiTypography sx={{ color: TC.secondary, fontSize: FS.sm }}>‹</VuiTypography>
                                    </VuiBox>
                                    {Array.from({ length: inboxTotalPages }, (_, i) => (
                                      <VuiBox
                                        key={i}
                                        onClick={() => { setInboxPage(i); setInboxEmail(null); }}
                                        px={0.8}
                                        py={0.3}
                                        sx={{
                                          cursor: "pointer",
                                          borderRadius: "7px",
                                          minWidth: 24,
                                          textAlign: "center",
                                          background: safePg === i ? "rgba(0,117,255,0.14)" : "transparent",
                                          border: `1px solid ${safePg === i ? "rgba(0,117,255,0.28)" : "#d8e6f7"}`,
                                          "&:hover": { background: "#f0f6ff" },
                                        }}
                                      >
                                        <VuiTypography sx={{ color: safePg === i ? "#0075ff" : TC.secondary, fontSize: "0.68rem", fontWeight: safePg === i ? FW.bold : FW.regular }}>
                                          {i + 1}
                                        </VuiTypography>
                                      </VuiBox>
                                    ))}
                                    <VuiBox
                                      onClick={() => { if (safePg < inboxTotalPages - 1) { setInboxPage(safePg + 1); setInboxEmail(null); } }}
                                      px={0.85}
                                      py={0.3}
                                      sx={{ cursor: safePg >= inboxTotalPages - 1 ? "default" : "pointer", borderRadius: "7px", border: "1px solid #d8e6f7", opacity: safePg >= inboxTotalPages - 1 ? 0.35 : 1, "&:hover": safePg < inboxTotalPages - 1 ? { background: "#f0f6ff" } : {} }}
                                    >
                                      <VuiTypography sx={{ color: TC.secondary, fontSize: FS.sm }}>›</VuiTypography>
                                    </VuiBox>
                                  </VuiBox>
                                </VuiBox>
                              )}
                            </VuiBox>

                            <VuiBox sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", background: "#ffffff" }}>
                              {activeEmail ? (() => {
                                const email = activeEmail;
                                const isIn = email.direction === "inbound";
                                const dateStr = formatDate(email.date);
                                return (
                                  <>
                                    <VuiBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={1.5} px={2} py={1.5} sx={{ borderBottom: "1px solid #e4eef8", background: "#f8fbff" }}>
                                      <VuiBox minWidth={0}>
                                        <VuiTypography variant="caption" sx={{ color: TC.secondary, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.62rem", display: "block", mb: 0.45 }}>
                                          {isIn ? "Received email" : "Sent email"}
                                        </VuiTypography>
                                        <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.9rem", fontWeight: FW.bold, lineHeight: 1.45, display: "block" }}>
                                          {email.subject || "(No subject)"}
                                        </VuiTypography>
                                        <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.72rem", lineHeight: 1.6, display: "block", mt: 0.45 }}>
                                          From: {email.from || "—"} &nbsp;•&nbsp; To: {email.to || "—"} &nbsp;•&nbsp; {dateStr || "—"}
                                        </VuiTypography>
                                      </VuiBox>
                                      <Tooltip title={inboxTab === 2 || email.sourceCollection === "deletedEmails" ? "Delete forever" : "Move to Deleted"} placement="top">
                                        <IconButton
                                          size="small"
                                          disabled={deletingEmailId === email.id}
                                          onClick={() => handleDeleteInboxEmail(email)}
                                          sx={{
                                            width: 34,
                                            height: 34,
                                            borderRadius: "10px",
                                            color: "#ff5252",
                                            bgcolor: "rgba(244,67,54,0.12)",
                                            border: "1px solid rgba(244,67,54,0.18)",
                                            "&:hover": { bgcolor: "rgba(244,67,54,0.2)" },
                                          }}
                                        >
                                          {inboxTab === 2 || email.sourceCollection === "deletedEmails"
                                            ? <DeleteForeverOutlinedIcon sx={{ fontSize: 18, color: "#ff5252" }} />
                                            : <DeleteOutlineRoundedIcon sx={{ fontSize: 18, color: "#ff5252" }} />}
                                        </IconButton>
                                      </Tooltip>
                                    </VuiBox>

                                    <VuiBox sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 2 }}>
                                      <VuiBox display="flex" flexWrap="wrap" gap={0.6} mb={1.5}>
                                        {renderMetaChips(email)}
                                        {email.reminderCount > 0 && (
                                          <Chip
                                            label={`${email.reminderCount} reminder${email.reminderCount !== 1 ? "s" : ""}`}
                                            size="small"
                                            sx={{ height: 18, fontSize: "0.58rem", fontWeight: FW.bold, bgcolor: "rgba(255,152,0,0.1)", color: "#ff9800", border: "1px solid rgba(255,152,0,0.2)" }}
                                          />
                                        )}
                                      </VuiBox>

                                      {email.bodyText ? (
                                        <VuiBox p={1.5} sx={{ background: "#f8fbff", borderRadius: "12px", border: "1px solid #d8e6f7", mb: 2 }}>
                                          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm, lineHeight: 1.7, whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
                                            {cleanEmailBodyText(email.bodyText)}
                                          </VuiTypography>
                                        </VuiBox>
                                      ) : (
                                        <VuiBox p={1.5} sx={{ background: "#f8fbff", borderRadius: "12px", border: "1px solid #d8e6f7", mb: 2 }}>
                                          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.72rem" }}>
                                            No message body available.
                                          </VuiTypography>
                                        </VuiBox>
                                      )}

                                      <VuiBox>
                                        <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary, fontSize: "0.62rem", textTransform: "uppercase", display: "block", mb: 0.9 }}>
                                          Attachments ({email.attachments?.length || 0})
                                        </VuiTypography>
                                        {email.attachments?.length > 0 ? (
                                          <VuiBox display="flex" flexDirection="column" gap={0.8}>
                                            {email.attachments.map((att, idx) => {
                                              const hasStorage = !!att.storagePath;
                                              const isImage = att.contentType?.startsWith("image/");
                                              const isPdf = att.contentType === "application/pdf";
                                              const isText = att.contentType?.startsWith("text/") || att.name?.endsWith(".txt") || att.name?.endsWith(".csv");
                                              const isOffice = /msword|wordprocessingml|ms-excel|spreadsheetml|ms-powerpoint|presentationml/.test(att.contentType || "")
                                                || /\.(doc|docx|xls|xlsx|ppt|pptx)$/i.test(att.name || "");
                                              const canPreview = hasStorage && (isImage || isPdf || isText || isOffice);
                                              const attIcon = isPdf
                                                ? <PictureAsPdfOutlinedIcon sx={{ fontSize: 16, color: "#ef9a9a" }} />
                                                : isImage
                                                  ? <ImageOutlinedIcon sx={{ fontSize: 16, color: "#81c784" }} />
                                                  : isOffice && /xls|xlsx|spreadsheet|csv/.test((att.contentType || "") + (att.name || ""))
                                                    ? <TableChartOutlinedIcon sx={{ fontSize: 16, color: "#ffd54f" }} />
                                                    : isOffice
                                                      ? <DescriptionOutlinedIcon sx={{ fontSize: 16, color: "#90caf9" }} />
                                                      : isText
                                                        ? <DescriptionOutlinedIcon sx={{ fontSize: 16, color: "#90caf9" }} />
                                                        : <AttachFileIcon sx={{ fontSize: 16, color: "#90caf9" }} />;

                                              const openAttachment = (action) => {
                                                if (!hasStorage) {
                                                  console.warn("[attachment] Skipping preview for attachment without storage");
                                                  return;
                                                }
                                                const win = action === "download" ? window.open("", "_blank") : null;
                                                setPreviewLoading(true);
                                                getAttachmentUrl(tenantId, profileId, email.id, idx, action)
                                                  .then((res) => {
                                                    if (action === "download") {
                                                      if (win) win.location.href = res.url;
                                                      return;
                                                    }
                                                    setPreviewAtt({ url: res.url, name: res.name, contentType: res.contentType });
                                                  })
                                                  .catch((err) => {
                                                    if (win) win.close();
                                                    console.error("[attachment] Failed to get URL:", err?.message);
                                                    setPreviewAtt({ url: null, name: att?.name || "(unknown)", contentType: att?.contentType || null, error: err?.message || "Failed to get URL" });
                                                  })
                                                  .finally(() => setPreviewLoading(false));
                                              };

                                              return (
                                                <VuiBox
                                                  key={idx}
                                                  display="flex"
                                                  alignItems="center"
                                                  gap={1}
                                                  px={1.1}
                                                  py={0.9}
                                                  sx={{
                                                    background: hasStorage ? "rgba(255,193,7,0.06)" : "rgba(255,255,255,0.6)",
                                                    border: `1px solid ${hasStorage ? "rgba(255,193,7,0.22)" : "#e1ebf7"}`,
                                                    borderRadius: "10px",
                                                  }}
                                                >
                                                  <VuiBox
                                                    sx={{
                                                      width: 30,
                                                      height: 30,
                                                      borderRadius: "9px",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                      flexShrink: 0,
                                                      background: hasStorage ? "rgba(255,193,7,0.14)" : "rgba(255,255,255,0.9)",
                                                    }}
                                                  >
                                                    {attIcon}
                                                  </VuiBox>
                                                  <VuiBox flex={1} minWidth={0}>
                                                    <VuiTypography variant="caption" sx={{ color: hasStorage ? TC.primary : TC.secondary, fontSize: "0.72rem", fontWeight: FW.bold, display: "block", lineHeight: 1.35, wordBreak: "break-word" }}>
                                                      {att.name}
                                                    </VuiTypography>
                                                    <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: "0.62rem" }}>
                                                      {att.size > 0
                                                        ? att.size < 1024
                                                          ? `${att.size} B`
                                                          : att.size < 1048576
                                                            ? `${(att.size / 1024).toFixed(1)} KB`
                                                            : `${(att.size / 1048576).toFixed(1)} MB`
                                                        : hasStorage
                                                          ? "Stored attachment"
                                                          : "File not stored"}
                                                    </VuiTypography>
                                                  </VuiBox>
                                                  {hasStorage ? (
                                                    <VuiBox display="flex" gap={0.5} flexShrink={0}>
                                                      <Tooltip title="Preview">
                                                        <span>
                                                          <VuiButton
                                                            size="small"
                                                            variant="outlined"
                                                            color="info"
                                                            onClick={() => openAttachment("preview")}
                                                            disabled={previewLoading || !canPreview}
                                                            startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                                                            sx={{ fontSize: "0.66rem", py: 0.25, px: 0.8, minWidth: 0, color: canPreview ? TC.link : "rgba(255,255,255,0.25)" }}
                                                          >
                                                            Preview
                                                          </VuiButton>
                                                        </span>
                                                      </Tooltip>
                                                      <Tooltip title="Download">
                                                        <span>
                                                          <IconButton
                                                            size="small"
                                                            onClick={() => openAttachment("download")}
                                                            disabled={previewLoading}
                                                            sx={{ color: TC.link, p: 0.4, "&:hover": { color: "#0075ff", background: "rgba(0,117,255,0.06)" } }}
                                                          >
                                                            <DownloadIcon sx={{ fontSize: 15 }} />
                                                          </IconButton>
                                                        </span>
                                                      </Tooltip>
                                                    </VuiBox>
                                                  ) : (
                                                    <Tooltip title={att.uploadError || "Not available"}>
                                                      <span style={{ fontSize: "0.7rem", color: "rgba(0,0,0,0.32)", flexShrink: 0, cursor: "help" }}>🔒</span>
                                                    </Tooltip>
                                                  )}
                                                </VuiBox>
                                              );
                                            })}
                                          </VuiBox>
                                        ) : (
                                          <VuiBox p={1.2} sx={{ background: "#f8fbff", borderRadius: "10px", border: "1px dashed #d8e6f7" }}>
                                            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.72rem" }}>
                                              No attachments on this email.
                                            </VuiTypography>
                                          </VuiBox>
                                        )}
                                      </VuiBox>
                                    </VuiBox>
                                  </>
                                );
                              })() : (
                                <VuiBox sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.1, px: 3, textAlign: "center" }}>
                                  <EmailIcon sx={{ color: "#90a4b8", fontSize: 38 }} />
                                  <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: "0.8rem", fontWeight: FW.bold }}>
                                    Select an email to read
                                  </VuiTypography>
                                  <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.72rem", lineHeight: 1.6, maxWidth: 260 }}>
                                    Choose a message from the list to view the full conversation and attachments.
                                  </VuiTypography>
                                </VuiBox>
                              )}
                            </VuiBox>
                          </VuiBox>
                        </>
                      );
                    })()}
                  </VuiBox></Card>
                )}

                {/* ── Tab 5: Services ───────────────────────────────── */}
                {profileTab === 5 && (
                  <Card sx={{ background: "#ffffff" }}><VuiBox p={3}>
                    <VuiTypography variant="button" fontWeight="bold"
                      sx={{ color: TC.primary,  textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em", display: "block", mb: 2 }}>
                      Services
                    </VuiTypography>
                    <CompanyServicesTab profile={profile} tenantId={tenantId} canManage={canManage} />
                  </VuiBox></Card>
                )}

                {/* ── Tab 6: Jobs & Tasks ───────────────────────────── */}
                {profileTab === 6 && (
                  <Card sx={{ background: "#ffffff" }}><VuiBox p={3}>
                    <CompanyJobsTab
                      key={location.key}
                      profile={profile}
                      tenantId={tenantId}
                      directors={directors}
                      initialJobTab={location.state?.jobTab ?? 0}
                      onProfileUpdated={() =>
                        getClientProfile(tenantId, profileId)
                          .then((data) => { const p = data?.clientProfile || data; if (p) setProfile(p); })
                          .catch(() => {})
                      }
                      onDirectorsUpdated={() =>
                        listDirectors(tenantId, profileId)
                          .then((res) => setDirectors(res?.directors ?? []))
                          .catch(() => {})
                      }
                    />
                  </VuiBox></Card>
                )}

                {/* ── Tab 7: Charges ─────────────────────────────────── */}
                {profileTab === 7 && (
                  <Card sx={{ background: "#ffffff" }}>
                    <VuiBox p={3}>
                      <ChargesTab tenantId={tenantId} clientId={profileId} />
                    </VuiBox>
                  </Card>
                )}

                {/* ── Tab 8: Documents ─────────────────────────────── */}
                {profileTab === 8 && (
                  <Card sx={{ background: "#ffffff" }}><VuiBox p={3}>
                    <DocumentsTab tenantId={tenantId} clientId={profileId} />
                  </VuiBox></Card>
                )}

                {/* ── Tab 9: Secrets (admin only) ──────────────────── */}
                {profileTab === 9 && (isTenantAdmin || isSuperAdmin) && (
                  <Card sx={{ background: "#ffffff" }}>
                    <VuiBox p={3}>
                      <SecretsTab tenantId={tenantId} clientId={profileId} />
                    </VuiBox>
                  </Card>
                )}

            </>
          </VuiBox>
        )}
      </VuiBox>

      <RecordRequestModal
        open={rrModalOpen}
        onClose={() => {
          setRrModalOpen(false);
          if (tenantId && profileId) {
            listRecordRequests(tenantId, profileId)
              .then((res) => setRecordRequests(res?.recordRequests ?? []))
              .catch(() => {});
          }
        }}
        tenantId={tenantId}
        clientId={profileId}
        profile={profile}
      />

      <Dialog
        open={filingPreview.open}
        onClose={closeFilingPreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: (theme) => ({ height: "90vh", borderRadius: "14px", background: theme.palette.background.paper, color: theme.palette.text.primary }) }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1, borderBottom: "1px solid #e4eef8" }}>
          <VuiTypography sx={{ color: TC.primary, fontWeight: FW.bold, fontSize: FS.base }}>
            📄 {filingPreview.title}
          </VuiTypography>
          <VuiBox display="flex" gap={1}>
            {filingPreview.blobUrl && (
              <>
                <Tooltip title="Print document">
                  <IconButton size="small"
                    onClick={() => {
                      const iframe = document.getElementById("filing-preview-iframe");
                      if (iframe?.contentWindow) { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
                      else { const w = window.open(filingPreview.blobUrl, "_blank"); w?.print(); }
                    }}
                    sx={{ color: TC.secondary, border: "1px solid #d8e6f7", borderRadius: "8px", px: 1.2, gap: 0.5 }}>
                    <PrintIcon sx={{ fontSize: 16 }} />
                    <VuiTypography sx={{ fontSize: FS.xs, color: TC.secondary, lineHeight: 1 }}>Print</VuiTypography>
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download PDF">
                  <IconButton size="small" component="a" href={filingPreview.blobUrl} download={`${filingPreview.title}.pdf`}
                    sx={{ color: TC.link, border: "1px solid #d8e6f7", borderRadius: "8px", px: 1.2, gap: 0.5 }}>
                    <DownloadIcon sx={{ fontSize: 16 }} />
                    <VuiTypography sx={{ fontSize: FS.xs, color: TC.link, lineHeight: 1 }}>Save</VuiTypography>
                  </IconButton>
                </Tooltip>
                <Tooltip title="Open in new tab">
                  <IconButton size="small" onClick={() => window.open(filingPreview.blobUrl, "_blank", "noopener,noreferrer")}
                    sx={{ color: TC.secondary, border: "1px solid #d8e6f7", borderRadius: "8px", px: 1.2, gap: 0.5 }}>
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                    <VuiTypography sx={{ fontSize: FS.xs, color: TC.secondary, lineHeight: 1 }}>New tab</VuiTypography>
                  </IconButton>
                </Tooltip>
              </>
            )}
            <IconButton size="small" onClick={closeFilingPreview}>
              <CloseIcon sx={{ fontSize: 18, color: TC.secondary }} />
            </IconButton>
          </VuiBox>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", height: "100%" }}>
          {filingPreview.loading && (
            <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1} gap={2}>
              <CircularProgress size={36} sx={{ color: TC.linkHover }} />
              <VuiTypography sx={{ color: TC.secondary, fontSize: FS.sm }}>Loading document from Companies House…</VuiTypography>
            </VuiBox>
          )}
          {filingPreview.error && (
            <VuiBox display="flex" alignItems="center" justifyContent="center" flex={1}>
              <VuiTypography sx={{ color: TC.error, fontSize: FS.md }}>⚠ {filingPreview.error}</VuiTypography>
            </VuiBox>
          )}
          {filingPreview.blobUrl && !filingPreview.loading && (
            <iframe
              id="filing-preview-iframe"
              src={filingPreview.blobUrl}
              title={filingPreview.title}
              style={{ width: "100%", flex: 1, border: "none", minHeight: 0 }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* ── Attachment preview Dialog ────────────────────────────────────── */}
      <Dialog
        open={!!previewAtt}
        onClose={() => setPreviewAtt(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { background: "#1a2035", border: "1px solid #d8e6f7", borderRadius: "12px", maxHeight: "90vh" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1, color: "#fff", fontSize: "0.9rem" }}>
          <span>
            {(() => {
              const kind = classifyAttachmentPreview(previewAtt?.contentType, previewAtt?.name);
              if (kind === "image") return "🖼 ";
              if (kind === "pdf") return "📄 ";
              if (kind === "office") return "📝 ";
              if (kind === "text") return "📃 ";
              return "📎 ";
            })()}
            {previewAtt?.name}
          </span>
          <VuiBox display="flex" gap={1} alignItems="center">
            {previewAtt?.url && (
              <VuiButton size="small" color="info" variant="outlined" sx={{ fontSize: "0.72rem" }}
                onClick={() => { const w = window.open("", "_blank"); if (w) w.location.href = previewAtt.url; }}>
                ⬇ Download
              </VuiButton>
            )}
            <IconButton onClick={() => setPreviewAtt(null)} sx={{ color: "rgba(255,255,255,0.5)", "&:hover": { color: "#fff" } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </VuiBox>
        </DialogTitle>
        <DialogContent sx={{ p: 1.5, display: "flex", justifyContent: "center", alignItems: "flex-start", overflow: "auto" }}>
          {/* Show a clear error message when previewAtt contains an error or no URL */}
          {previewAtt?.error && (
            <VuiBox display="flex" alignItems="center" justifyContent="center" width="100%" py={6}>
              <VuiTypography sx={{ color: TC.error, fontSize: "0.95rem" }}>⚠ {previewAtt.error}</VuiTypography>
            </VuiBox>
          )}

          {(!previewAtt?.error) && (() => {
            const nm  = previewAtt?.name || "";
            const url = previewAtt?.url || "";

            if (!url) {
              return (
                <VuiBox textAlign="center" py={4}>
                  <VuiTypography sx={{ color: TC.secondary, fontSize: "0.82rem", mb: 2 }}>
                    Attachment URL not available.
                  </VuiTypography>
                  <VuiTypography sx={{ color: TC.tertiary, fontSize: "0.78rem" }}>
                    Try downloading the file from the original email source or check if the attachment was uploaded correctly.
                  </VuiTypography>
                </VuiBox>
              );
            }

            const kind = classifyAttachmentPreview(previewAtt?.contentType, nm);

            if (kind === "image") {
              return (
                <img src={url} alt={nm}
                  style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain", borderRadius: "8px" }} />
              );
            }
            if (kind === "pdf" || kind === "text") {
              // Browsers render PDFs and plain text natively in iframes
              return (
                <iframe src={url} title={nm}
                  style={{ width: "100%", height: "75vh", border: "none", borderRadius: "8px", background: "#fff" }} />
              );
            }
            if (kind === "office") {
              // Use Google Docs viewer for Office formats
              const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
              return (
                <iframe src={viewerUrl} title={nm}
                  style={{ width: "100%", height: "75vh", border: "none", borderRadius: "8px", background: "#fff" }} />
              );
            }
            return (
              <VuiBox textAlign="center" py={4}>
                <VuiTypography sx={{ color: TC.secondary, fontSize: "0.82rem", mb: 2 }}>
                  Preview not available for this file type.
                </VuiTypography>
                {url && (
                  <VuiButton color="info" variant="outlined"
                    onClick={() => { const w = window.open("", "_blank"); if (w) w.location.href = url; }}>
                    ⬇ Download {nm}
                  </VuiButton>
                )}
              </VuiBox>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Footer />

      {/* Floating add buttons */}
      {canManage && (
        <VuiBox sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 99, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Tooltip title="New Company" placement="left">
            <Fab size="medium" sx={{ bgcolor: "#0075ff", color: "#fff", "&:hover": { bgcolor: "#0060d0" } }}
              onClick={() => history.push("/client-profiles?create=company")}>
              <BusinessIcon />
            </Fab>
          </Tooltip>
          <Tooltip title="New Individual" placement="left">
            <Fab size="medium" sx={{ bgcolor: "#4caf50", color: "#fff", "&:hover": { bgcolor: "#388e3c" } }}
              onClick={() => history.push("/client-profiles?create=individual")}>
              <PersonAddIcon />
            </Fab>
          </Tooltip>
        </VuiBox>
      )}
    </DashboardLayout>
  );
}

